import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { reportAPI, branchAPI, categoryAPI, currencyAPI, vendorAPI, itemCommissionAPI, userAPI, expenseAPI, expenseCategoryAPI } from '../utils/api';
import { fmt, fmtDate, PageHeader } from '../components/common';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../i18n';

import {
  DollarSign, ShoppingCart, Package, Users, TrendingUp, TrendingDown,
  BarChart2, CreditCard, Award, Tag, Building2, Warehouse,
  RefreshCw, Download, Filter, X, ChevronDown, ChevronUp,
  Clock, CheckCircle, AlertTriangle, FileText, Lock, Layers,
  ArrowUpRight, Percent, Star, Truck, ArrowLeftRight, ClipboardList, Globe
} from 'lucide-react';
import toast from 'react-hot-toast';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

// ── Helpers ────────────────────────────────────────────────────────
const today = () => new Date().toISOString().split('T')[0];
const monthStart = () => { const d = new Date(); d.setDate(1); return d.toISOString().split('T')[0] };
const fmtNum = n => n != null ? parseFloat(n).toFixed(2) : '0.00';
const COLORS = ['#6366f1', '#22c55e', '#f59e0b', '#3b82f6', '#ec4899', '#14b8a6', '#f97316', '#8b5cf6', '#06b6d4', '#84cc16'];

// ── Shared components ──────────────────────────────────────────────
const KpiCard = ({ icon: Icon, label, value, sub, color = 'var(--primary)', small }) => (
  <div style={{ background: 'var(--surface)', borderRadius: 12, padding: small ? '12px 14px' : '16px 18px', border: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 4 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 2 }}>
      <div style={{ width: 30, height: 30, borderRadius: 8, background: color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon size={15} color={color} />
      </div>
      <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--td)', textTransform: 'uppercase', letterSpacing: .5 }}>{label}</span>
    </div>
    <div style={{ fontSize: small ? 16 : 22, fontWeight: 900, color }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: 'var(--td)' }}>{sub}</div>}
  </div>
);

const SectionTitle = ({ children, icon: Icon, color = 'var(--primary)' }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, paddingBottom: 8, borderBottom: '2px solid var(--border)' }}>
    {Icon && <div style={{ width: 28, height: 28, borderRadius: 7, background: color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon size={14} color={color} /></div>}
    <span style={{ fontSize: 15, fontWeight: 800, color: 'var(--text)' }}>{children}</span>
  </div>
);

const Table = ({ cols, data, emptyMsg = 'No data' }) => (
  <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr style={{ background: 'var(--s2)' }}>
          {cols.map((c, i) => <th key={i} style={{ padding: '8px 12px', fontSize: 10, fontWeight: 700, color: 'var(--td)', textTransform: 'uppercase', letterSpacing: .4, textAlign: c.right ? 'right' : 'left', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{c.label}</th>)}
        </tr>
      </thead>
      <tbody>
        {!data?.length ? <tr><td colSpan={cols.length} style={{ padding: '28px 12px', textAlign: 'center', color: 'var(--td)', fontSize: 13 }}>{emptyMsg}</td></tr>
          : data.map((row, i) => (
            <tr key={i} style={{ borderBottom: '1px solid var(--border)', background: i % 2 === 0 ? 'var(--surface)' : 'var(--s2)' }}>
              {cols.map((c, j) => <td key={j} style={{ padding: '9px 12px', fontSize: 12, textAlign: c.right ? 'right' : 'left', fontWeight: c.bold ? 700 : 400, color: c.color ? c.color(row) : 'var(--text)' }}>{c.render ? c.render(row) : row[c.key] ?? '—'}</td>)}
            </tr>
          ))}
      </tbody>
    </table>
  </div>
);

const StatusBadge = ({ status }) => {
  const s = { pending: { c: '#d97706', bg: '#fef3c7' }, approved: { c: '#2563eb', bg: '#dbeafe' }, paid: { c: '#16a34a', bg: '#dcfce7' }, cancelled: { c: '#6b7280', bg: '#f3f4f6' }, draft: { c: '#64748b', bg: '#f1f5f9' }, sent: { c: '#2563eb', bg: '#dbeafe' }, partial: { c: '#d97706', bg: '#fef3c7' }, overdue: { c: '#dc2626', bg: '#fee2e2' }, completed: { c: '#16a34a', bg: '#dcfce7' }, confirmed: { c: '#2563eb', bg: '#dbeafe' } };
  const cfg = s[status] || { c: '#64748b', bg: '#f1f5f9' };
  return <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 700, background: cfg.bg, color: cfg.c, whiteSpace: 'nowrap' }}>{status}</span>;
};

function ExportCSV({ data, columns, filename }) {
  const { t } = useLang();
  const doExport = () => {
    if (!data?.length) return toast.error('No data to export');
    const headers = columns.map(c => c.label || c.key).join(',');
    const rows = data.map(row => columns.map(c => { const v = c.render ? c.render(row) : row[c.key]; return typeof v === 'string' && v.includes(',') ? `"${v}"` : v ?? '' }).join(','));
    const csv = [headers, ...rows].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a'); a.href = url; a.download = `${filename}-${today()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };
  return <button className="btn btn-ghost btn-sm" onClick={doExport} style={{ gap: 5, fontSize: 12 }}><Download size={13} /> {t('reports.exportCsv')}</button>;
}

function DateRangeBar({ dateFrom, dateTo, onFrom, onTo, onRun, loading, children, branches, branchId, onBranch, isOwner }) {
  const { t } = useLang();
  const last30 = new Date(); last30.setDate(last30.getDate() - 30);

  const last30str = last30.toISOString().split('T')[0];
  const presets = [['Today', today(), today()], ['This Month', monthStart(), today()], ['Last 30d', last30str, today()], ['This Year', `${new Date().getFullYear()}-01-01`, today()]];
  return (
    <div style={{ background: 'var(--surface)', borderRadius: 12, padding: '12px 16px', border: '1px solid var(--border)', marginBottom: 16, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-end' }}>
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center', flex: 1, minWidth: 200 }}>
        {presets.map(([label, from, to]) => (
          <button key={label} className="btn btn-ghost btn-sm" style={{ fontSize: 11, padding: '4px 10px', whiteSpace: 'nowrap' }}
            onClick={() => { onFrom(from); onTo(to) }}>{label}</button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        {isOwner && branches?.length > 0 && <select value={branchId || ''} onChange={e => onBranch(e.target.value)} style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--s2)', color: 'var(--text)', fontSize: 12 }}>
          <option value="">All Branches</option>
          {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>}
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <input type="date" value={dateFrom} onChange={e => onFrom(e.target.value)} style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--s2)', color: 'var(--text)', fontSize: 12 }} />
          <span style={{ color: 'var(--td)', fontSize: 12 }}>to</span>
          <input type="date" value={dateTo} onChange={e => onTo(e.target.value)} style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--s2)', color: 'var(--text)', fontSize: 12 }} />
        </div>
        <button className="btn btn-primary btn-sm" onClick={onRun} disabled={loading} style={{ gap: 5, fontSize: 12, height: 36, whiteSpace: 'nowrap' }}>
          {loading ? <><RefreshCw size={12} style={{ animation: 'spin 1s linear infinite' }} /> Loading…</> : <><Filter size={12} /> Run Report</>}
        </button>
        {children}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// 1. POS SALES SUMMARY
// ════════════════════════════════════════════════════════════════
function PosSummaryTab({ branches, isOwner, myBranchId }) {
  const { t } = useLang();
  const [df, setDf] = useState(monthStart()); const [dt, setDt] = useState(today()); const [bid, setBid] = useState('');
  const [data, setData] = useState(null); const [ld, setLd] = useState(false);
  const run = async () => { setLd(true); try { const r = await reportAPI.posSummary({ date_from: df, date_to: dt, ...(isOwner && bid ? { branch_id: bid } : {}) }); setData(r.data); } catch { toast.error('Failed to load POS summary') } finally { setLd(false) } };
  useEffect(() => { run() }, []);
  return (
    <div>
      <DateRangeBar dateFrom={df} dateTo={dt} onFrom={setDf} onTo={setDt} onRun={run} loading={ld} branches={branches} branchId={bid} onBranch={setBid} isOwner={isOwner}>
        {data && <ExportCSV data={data.payment_methods} columns={[{ key: 'name', label: 'Method' }, { key: 'count', label: 'Count' }, { key: 'amount', label: 'Amount' }]} filename="pos-payment-methods" />}
      </DateRangeBar>
      {data && <>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))', gap: 10, marginBottom: 20 }}>
          <KpiCard icon={ShoppingCart} label={t('reports.transactions')} value={data.total_transactions} color="var(--primary)" />
          <KpiCard icon={DollarSign} label={t('reports.totalSales')} value={fmt(data.total_sales)} sub={`Avg ${fmt(data.avg_order)}`} color="var(--success)" />
          <KpiCard icon={TrendingDown} label={t('reports.totalDiscount')} value={fmt(data.total_discount)} color="var(--warning)" />
          <KpiCard icon={BarChart2} label={t('reports.avgOrder')} value={fmt(data.avg_order)} color="var(--info)" />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
          <div className="card" style={{ padding: 16 }}>
            <SectionTitle icon={CreditCard} color="var(--info)">{t('reports.paymentMethodBreakdown')}</SectionTitle>
            {data.payment_methods?.length > 0 && <ResponsiveContainer width="100%" height={200}><PieChart><Pie data={data.payment_methods} dataKey="amount" nameKey="name" cx="50%" cy="50%" outerRadius={80} paddingAngle={3}>{data.payment_methods.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip formatter={v => fmt(v)} /><Legend /></PieChart></ResponsiveContainer>}
            <Table cols={[{ key: 'name', label: 'Method' }, { key: 'count', label: 'Txns', right: true }, { key: 'amount', label: 'Amount', right: true, render: r => fmt(r.amount) }]} data={data.payment_methods} />
          </div>
          <div className="card" style={{ padding: 16 }}>
            <SectionTitle icon={Building2} color="var(--primary)">{t('reports.byBranch')}</SectionTitle>
            {data.by_branch?.length > 0 && <ResponsiveContainer width="100%" height={200}><BarChart data={data.by_branch}><XAxis dataKey="name" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} /><Tooltip formatter={v => fmt(v)} /><Bar dataKey="total" fill="#6366f1" radius={[4, 4, 0, 0]} /></BarChart></ResponsiveContainer>}
            <Table cols={[{ key: 'name', label: 'Branch' }, { key: 'transactions', label: 'Txns', right: true }, { key: 'total', label: 'Revenue', right: true, bold: true, render: r => fmt(r.total) }]} data={data.by_branch} />
          </div>
        </div>
      </>}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// 2. POS SESSIONS
// ════════════════════════════════════════════════════════════════
function PosSessionsTab({ branches, isOwner }) {
  const { t } = useLang();
  const [df, setDf] = useState(monthStart()); const [dt, setDt] = useState(today()); const [bid, setBid] = useState(''); const [status, setStatus] = useState('');
  const [data, setData] = useState(null); const [ld, setLd] = useState(false);
  const run = async () => { setLd(true); try { const r = await reportAPI.posSessions({ date_from: df, date_to: dt, ...(status && { status }), ...(isOwner && bid ? { branch_id: bid } : {}) }); setData(r.data); } catch { toast.error('Failed') } finally { setLd(false) } };
  useEffect(() => { run() }, []);
  const exportCols = [{ key: 'id', label: 'Session ID' }, { key: 'branch', label: 'Branch', render: r => r.branch?.name || '—' }, { key: 'cashier', label: 'Cashier', render: r => r.cashier?.name || '—' }, { key: 'opened_at', label: 'Opened', render: r => fmtDate(r.opened_at) }, { key: 'closed_at', label: 'Closed', render: r => fmtDate(r.closed_at) }, { key: 'opening_balance', label: 'Opening $', render: r => fmtNum(r.opening_balance) }, { key: 'closing_balance', label: 'Closing $', render: r => fmtNum(r.closing_balance) }, { key: 'expected_balance', label: 'Expected $', render: r => fmtNum(r.expected_balance) }, { key: 'difference', label: 'Difference $', render: r => fmtNum(r.difference) }, { key: 'total_sales', label: 'Sales $', render: r => fmtNum(r.total_sales) }, { key: 'total_transactions', label: 'Txns' }];
  return (
    <div>
      <DateRangeBar dateFrom={df} dateTo={dt} onFrom={setDf} onTo={setDt} onRun={run} loading={ld} branches={branches} branchId={bid} onBranch={setBid} isOwner={isOwner}>
        <select value={status} onChange={e => setStatus(e.target.value)} style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--s2)', color: 'var(--text)', fontSize: 12 }}>
          <option value="">All Status</option><option value="open">Open</option><option value="closed">Closed</option>
        </select>
        {data && <ExportCSV data={data.sessions} columns={exportCols} filename="pos-sessions" />}
      </DateRangeBar>
      {data && <>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))', gap: 10, marginBottom: 16 }}>
          <KpiCard icon={Clock} label={t('reports.totalSessions')} value={data.total_sessions} color="var(--primary)" />
          <KpiCard icon={DollarSign} label={t('reports.totalSales')} value={fmt(data.total_sales)} color="var(--success)" />
          <KpiCard icon={AlertTriangle} label={t('reports.totalVariance')} value={fmt(Math.abs(data.total_difference))} sub={data.total_difference >= 0 ? 'Over' : 'Short'} color={data.total_difference >= 0 ? 'var(--success)' : 'var(--danger)'} />
        </div>
        <div className="card" style={{ padding: 16 }}>
          <SectionTitle icon={Clock} color="var(--primary)">{t('reports.sessionDetails')}</SectionTitle>
          <Table cols={[
            { key: 'id', label: 'Session', render: r => <span style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--primary)' }}>#{r.id}</span> },
            { key: 'branch', label: 'Branch', render: r => r.branch?.name || '—' },
            { key: 'cashier', label: 'Cashier', render: r => r.cashier?.name || '—' },
            { key: 'status', label: 'Status', render: r => <StatusBadge status={r.status} /> },
            { key: 'opened_at', label: 'Opened', render: r => fmtDate(r.opened_at) },
            { key: 'closed_at', label: 'Closed', render: r => r.closed_at ? fmtDate(r.closed_at) : <span style={{ color: 'var(--success)', fontWeight: 700 }}>Open</span> },
            { key: 'opening_balance', label: 'Opening', right: true, render: r => fmt(r.opening_balance) },
            { key: 'closing_balance', label: 'Closing', right: true, render: r => r.closing_balance ? fmt(r.closing_balance) : '—' },
            { key: 'expected_balance', label: 'Expected', right: true, render: r => fmt(r.expected_balance) },
            { key: 'difference', label: 'Variance', right: true, bold: true, color: r => r.difference >= 0 ? 'var(--success)' : 'var(--danger)', render: r => `${r.difference >= 0 ? '+' : ''}${fmt(r.difference)}` },
            { key: 'total_sales', label: 'Sales', right: true, bold: true, render: r => fmt(r.total_sales) },
            { key: 'total_transactions', label: 'Txns', right: true },
          ]} data={data.sessions} />
        </div>
      </>}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// 3. SALES ORDERS
// ════════════════════════════════════════════════════════════════
function SalesOrdersTab({ branches, isOwner }) {
  const { t } = useLang();
  const [df, setDf] = useState(monthStart()); const [dt, setDt] = useState(today()); const [bid, setBid] = useState(''); const [status, setStatus] = useState('');
  const [data, setData] = useState(null); const [ld, setLd] = useState(false);
  const run = async () => { setLd(true); try { const r = await reportAPI.salesOrders({ date_from: df, date_to: dt, ...(status && { status }), ...(isOwner && bid ? { branch_id: bid } : {}) }); setData(r.data); } catch { toast.error('Failed') } finally { setLd(false) } };
  useEffect(() => { run() }, []);
  return (
    <div>
      <DateRangeBar dateFrom={df} dateTo={dt} onFrom={setDf} onTo={setDt} onRun={run} loading={ld} branches={branches} branchId={bid} onBranch={setBid} isOwner={isOwner}>
        <select value={status} onChange={e => setStatus(e.target.value)} style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--s2)', color: 'var(--text)', fontSize: 12 }}>
          <option value="">All Status</option>{['draft', 'pending', 'confirmed', 'processing', 'shipped', 'completed', 'cancelled'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        {data && <ExportCSV data={data.orders} columns={[{ key: 'order_number', label: 'Order #' }, { key: 'date', label: 'Date', render: r => fmtDate(r.date) }, { key: 'customer', label: 'Customer' }, { key: 'branch', label: 'Branch' }, { key: 'status', label: 'Status' }, { key: 'subtotal', label: 'Subtotal' }, { key: 'discount', label: 'Discount' }, { key: 'tax', label: 'Tax' }, { key: 'total', label: 'Total' }]} filename="sales-orders" />}
      </DateRangeBar>
      {data && <>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))', gap: 10, marginBottom: 16 }}>
          <KpiCard icon={ShoppingCart} label={t('reports.totalOrders')} value={data.summary?.total_orders} color="var(--primary)" />
          <KpiCard icon={DollarSign} label={t('reports.revenue')} value={fmt(data.summary?.total_revenue)} color="var(--success)" />
          <KpiCard icon={TrendingDown} label={t('reports.discount')} value={fmt(data.summary?.total_discount)} color="var(--warning)" />
          <KpiCard icon={BarChart2} label={t('reports.avgOrder')} value={fmt(data.summary?.avg_order)} color="var(--info)" />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div className="card" style={{ padding: 16 }}>
            <SectionTitle icon={Star} color="var(--accent)">{t('reports.topProducts')}</SectionTitle>
            <Table cols={[{ key: 'name', label: 'Product' }, { key: 'sku', label: 'SKU' }, { key: 'qty', label: 'Qty Sold', right: true, bold: true }, { key: 'revenue', label: 'Revenue', right: true, bold: true, render: r => fmt(r.revenue) }]} data={data.top_products?.slice(0, 8)} emptyMsg="No product data" />
          </div>
          <div className="card" style={{ padding: 16 }}>
            <SectionTitle icon={Users} color="var(--info)">{t('reports.topCustomers')}</SectionTitle>
            <Table cols={[{ key: 'name', label: 'Customer' }, { key: 'orders', label: t('reports.tabs.salesOrders'), right: true }, { key: 'total', label: 'Total', right: true, bold: true, render: r => fmt(r.total) }]} data={data.top_customers?.slice(0, 8)} emptyMsg="No customer data" />
          </div>
        </div>
        <div className="card" style={{ padding: 16 }}>
          <SectionTitle icon={FileText} color="var(--primary)">{t('reports.orderList')}</SectionTitle>
          <Table cols={[{ key: 'order_number', label: 'Order #', render: r => <span style={{ fontFamily: 'monospace', color: 'var(--primary)', fontWeight: 700 }}>{r.order_number}</span> }, { key: 'date', label: 'Date', render: r => fmtDate(r.date) }, { key: 'customer', label: 'Customer' }, { key: 'branch', label: 'Branch' }, { key: 'status', label: 'Status', render: r => <StatusBadge status={r.status} /> }, { key: 'discount', label: 'Discount', right: true, render: r => fmt(r.discount) }, { key: 'total', label: 'Total', right: true, bold: true, render: r => fmt(r.total) }, { key: 'items_count', label: 'Items', right: true }]} data={data.orders} />
        </div>
      </>}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// 4. INVOICE REPORT
// ════════════════════════════════════════════════════════════════
function SalesInvoicesTab({ branches, isOwner }) {
  const { t } = useLang();
  const [df, setDf] = useState(monthStart()); const [dt, setDt] = useState(today()); const [bid, setBid] = useState(''); const [status, setStatus] = useState('');
  const [data, setData] = useState(null); const [ld, setLd] = useState(false);
  const run = async () => { setLd(true); try { const r = await reportAPI.salesInvoices({ date_from: df, date_to: dt, ...(status && { status }), ...(isOwner && bid ? { branch_id: bid } : {}) }); setData(r.data); } catch { toast.error('Failed') } finally { setLd(false) } };
  useEffect(() => { run() }, []);
  const byStatusData = data ? Object.entries(data.summary?.by_status || {}).map(([k, v]) => ({ name: k, count: v.count, total: v.total })) : [];
  return (
    <div>
      <DateRangeBar dateFrom={df} dateTo={dt} onFrom={setDf} onTo={setDt} onRun={run} loading={ld} branches={branches} branchId={bid} onBranch={setBid} isOwner={isOwner}>
        <select value={status} onChange={e => setStatus(e.target.value)} style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--s2)', color: 'var(--text)', fontSize: 12 }}>
          <option value="">All Status</option>{['draft', 'sent', 'paid', 'partial', 'overdue', 'cancelled'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        {data && <ExportCSV data={data.invoices} columns={[{ key: 'invoice_number', label: 'Invoice #' }, { key: 'date', label: 'Date' }, { key: 'customer', label: 'Customer' }, { key: 'branch', label: 'Branch' }, { key: 'status', label: 'Status' }, { key: 'subtotal', label: 'Subtotal' }, { key: 'tax', label: 'Tax' }, { key: 'discount', label: 'Discount' }, { key: 'total', label: 'Total' }, { key: 'paid', label: 'Paid' }, { key: 'balance', label: 'Balance' }]} filename="invoices" />}
      </DateRangeBar>
      {data && <>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(150px,1fr))', gap: 10, marginBottom: 16 }}>
          <KpiCard icon={FileText} label={t('reports.totalInvoices')} value={data.summary?.total_invoices} color="var(--primary)" />
          <KpiCard icon={DollarSign} label={t('reports.totalRevenue')} value={fmt(data.summary?.total_revenue)} color="var(--success)" />
          <KpiCard icon={CheckCircle} label={t('reports.totalPaid')} value={fmt(data.summary?.total_paid)} color="var(--success)" />
          <KpiCard icon={AlertTriangle} label={t('reports.outstanding')} value={fmt(data.summary?.total_outstanding)} color="var(--danger)" />
          <KpiCard icon={Percent} label={t('reports.taxCollected')} value={fmt(data.summary?.total_tax)} color="var(--info)" />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div className="card" style={{ padding: 16 }}>
            <SectionTitle icon={BarChart2} color="var(--primary)">{t('reports.invoiceStatusBreakdown')}</SectionTitle>
            {byStatusData.length > 0 && <ResponsiveContainer width="100%" height={180}><BarChart data={byStatusData}><XAxis dataKey="name" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} /><Tooltip formatter={v => fmt(v)} /><Bar dataKey="total" fill="#6366f1" radius={[4, 4, 0, 0]} /></BarChart></ResponsiveContainer>}
            <Table cols={[{ key: 'name', label: 'Status', render: r => <StatusBadge status={r.name} /> }, { key: 'count', label: 'Count', right: true }, { key: 'total', label: 'Amount', right: true, bold: true, render: r => fmt(r.total) }]} data={byStatusData} />
          </div>
          <div className="card" style={{ padding: 16 }}>
            <SectionTitle icon={Star} color="var(--accent)">{t('reports.topProducts')}</SectionTitle>
            <Table cols={[{ key: 'name', label: 'Product' }, { key: 'sku', label: 'SKU' }, { key: 'qty', label: 'Qty', right: true }, { key: 'revenue', label: 'Revenue', right: true, bold: true, render: r => fmt(r.revenue) }]} data={data.top_products?.slice(0, 8)} emptyMsg="No data" />
          </div>
        </div>
        <div className="card" style={{ padding: 16 }}>
          <SectionTitle icon={FileText} color="var(--primary)">{t('reports.invoiceList')}</SectionTitle>
          <Table cols={[{ key: 'invoice_number', label: 'Invoice #', render: r => <span style={{ fontFamily: 'monospace', color: 'var(--primary)', fontWeight: 700 }}>{r.invoice_number}</span> }, { key: 'date', label: 'Date', render: r => fmtDate(r.date) }, { key: 'customer', label: 'Customer' }, { key: 'branch', label: 'Branch' }, { key: 'status', label: 'Status', render: r => <StatusBadge status={r.status} /> }, { key: 'subtotal', label: 'Subtotal', right: true, render: r => fmt(r.subtotal) }, { key: 'tax', label: 'Tax', right: true, render: r => fmt(r.tax) }, { key: 'total', label: 'Total', right: true, bold: true, render: r => fmt(r.total) }, { key: 'paid', label: 'Paid', right: true, render: r => <span style={{ color: 'var(--success)', fontWeight: 600 }}>{fmt(r.paid)}</span> }, { key: 'balance', label: 'Balance', right: true, render: r => <span style={{ color: r.balance > 0 ? 'var(--danger)' : 'var(--success)', fontWeight: 700 }}>{fmt(r.balance)}</span> }]} data={data.invoices} />
        </div>
      </>}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// 5. CUSTOMER REPORT
// ════════════════════════════════════════════════════════════════
function CustomerReportTab({ branches, isOwner }) {
  const { t } = useLang();
  const [df, setDf] = useState(monthStart()); const [dt, setDt] = useState(today()); const [bid, setBid] = useState('');
  const [data, setData] = useState(null); const [ld, setLd] = useState(false);
  const run = async () => { setLd(true); try { const r = await reportAPI.customerSummary({ date_from: df, date_to: dt, ...(isOwner && bid ? { branch_id: bid } : {}) }); setData(r.data); } catch { toast.error('Failed') } finally { setLd(false) } };
  useEffect(() => { run() }, []);
  const top10 = useMemo(() => data?.customers?.slice(0, 10) || [], [data]);
  return (
    <div>
      <DateRangeBar dateFrom={df} dateTo={dt} onFrom={setDf} onTo={setDt} onRun={run} loading={ld} branches={branches} branchId={bid} onBranch={setBid} isOwner={isOwner}>
        {data && <ExportCSV data={data.customers} columns={[{ key: 'name', label: 'Customer' }, { key: 'phone', label: 'Phone' }, { key: 'pos_txns', label: 'POS Txns' }, { key: 'pos_revenue', label: 'POS Revenue', render: r => fmtNum(r.pos_revenue) }, { key: 'invoice_count', label: t('reports.tabs.salesInvoices') }, { key: 'invoice_revenue', label: 'Invoice Revenue', render: r => fmtNum(r.invoice_revenue) }, { key: 'total', label: 'Total Revenue', render: r => fmtNum(r.total) }, { key: 'last_purchase', label: 'Last Purchase', render: r => fmtDate(r.last_purchase) }]} filename="customer-report" />}
      </DateRangeBar>
      {data && <>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))', gap: 10, marginBottom: 16 }}>
          <KpiCard icon={Users} label={t('reports.uniqueCustomers')} value={data.total_customers} color="var(--primary)" />
          <KpiCard icon={DollarSign} label={t('reports.customerRevenue')} value={fmt(data.total_revenue)} color="var(--success)" />
          <KpiCard icon={TrendingUp} label={t('reports.avgPerCustomer')} value={fmt(data.avg_per_customer)} color="var(--info)" />
          <KpiCard icon={ShoppingCart} label={t('reports.walkInRevenue')} value={fmt(data.walk_in_revenue)} color="var(--warning)" />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div className="card" style={{ padding: 16 }}>
            <SectionTitle icon={Award} color="var(--accent)">{t('reports.top10Customers')}</SectionTitle>
            {top10.length > 0 && <ResponsiveContainer width="100%" height={200}><BarChart layout="vertical" data={top10}><XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={v => fmt(v)} /><YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={90} /><Tooltip formatter={v => fmt(v)} /><Bar dataKey="total" fill="#22c55e" radius={[0, 4, 4, 0]} /></BarChart></ResponsiveContainer>}
          </div>
          <div className="card" style={{ padding: 16 }}>
            <SectionTitle icon={Users} color="var(--primary)">{t('reports.customerBreakdown')}</SectionTitle>
            <Table cols={[{ key: 'name', label: 'Customer' }, { key: 'phone', label: 'Phone' }, { key: 'pos_txns', label: 'POS', right: true }, { key: 'invoice_count', label: t('reports.tabs.salesInvoices'), right: true }, { key: 'total', label: 'Total', right: true, bold: true, render: r => fmt(r.total) }, { key: 'last_purchase', label: 'Last', render: r => r.last_purchase ? fmtDate(r.last_purchase) : '—' }]} data={data.customers?.slice(0, 15)} emptyMsg="No customers" />
          </div>
        </div>
      </>}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// 6. PRODUCT SALES REPORT
// ════════════════════════════════════════════════════════════════
function ProductSalesTab({ branches, isOwner }) {
  const { t } = useLang();
  const [df, setDf] = useState(monthStart()); const [dt, setDt] = useState(today());
  const [bid, setBid] = useState(''); const [cats, setCats] = useState([]); const [catId, setCatId] = useState('');
  const [data, setData] = useState(null); const [ld, setLd] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});

  useEffect(() => { categoryAPI.getAll({ limit: 100 }).then(r => setCats(r.data.data || [])).catch(() => { }) }, []);
  const run = async () => {
    setLd(true);
    try {
      const r = await reportAPI.productSales({ date_from: df, date_to: dt, ...(catId && { category_id: catId }), ...(isOwner && bid ? { branch_id: bid } : {}) });
      setData(r.data);
      setExpandedRows({});
    } catch { toast.error('Failed') } finally { setLd(false) }
  };
  useEffect(() => { run() }, []);

  const toggleRow = (id) => setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  const top10 = data?.products?.slice(0, 10) || [];

  // Flatten for CSV export including uom rows
  const csvData = useMemo(() => {
    if (!data?.products) return [];
    return data.products.flatMap(p => {
      const base = { name: p.name, sku: p.sku, category: p.category, base_uom: p.base_uom, total_qty: p.total_qty, pos_qty: p.pos_qty, inv_qty: p.inv_qty, total_revenue: p.total_revenue, profit: p.profit, margin: `${p.margin}%`, uom_name: '', uom_abbr: '', uom_qty: '', uom_revenue: '', avg_unit_price: '' };
      const uomRows = (p.uom_breakdown || []).map(u => ({ name: `  └ ${p.name}`, sku: p.sku, category: p.category, base_uom: p.base_uom, total_qty: '', pos_qty: u.pos_qty, inv_qty: u.inv_qty, total_revenue: '', profit: '', margin: '', uom_name: u.uom_name, uom_abbr: u.uom_abbr, uom_qty: u.total_qty, uom_revenue: u.total_revenue, avg_unit_price: u.avg_unit_price }));
      return [base, ...uomRows];
    });
  }, [data]);

  const uomBadge = (abbr, convFactor) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 20, background: 'var(--pm)', color: 'var(--primary)', border: '1px solid rgba(99,102,241,.2)' }}>
      {abbr}{convFactor && convFactor !== 1 ? <span style={{ opacity: .7 }}>×{convFactor}</span> : null}
    </span>
  );

  return (
    <div>
      <DateRangeBar dateFrom={df} dateTo={dt} onFrom={setDf} onTo={setDt} onRun={run} loading={ld} branches={branches} branchId={bid} onBranch={setBid} isOwner={isOwner}>
        <select value={catId} onChange={e => setCatId(e.target.value)} style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--s2)', color: 'var(--text)', fontSize: 12 }}>
          <option value="">{t('reports.allCategories')}</option>{cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {data && <ExportCSV data={csvData} columns={[{ key: 'name', label: 'Product' }, { key: 'sku', label: 'SKU' }, { key: 'category', label: 'Category' }, { key: 'base_uom', label: 'Base UoM' }, { key: 'uom_name', label: 'Sold UoM' }, { key: 'uom_abbr', label: 'UoM Abbr' }, { key: 'uom_qty', label: 'UoM Qty' }, { key: 'avg_unit_price', label: 'Avg Unit Price' }, { key: 'total_qty', label: 'Total Qty' }, { key: 'pos_qty', label: 'POS Qty' }, { key: 'inv_qty', label: 'Invoice Qty' }, { key: 'total_revenue', label: 'Revenue' }, { key: 'profit', label: 'Profit' }, { key: 'margin', label: 'Margin %' }]} filename="product-sales" />}
      </DateRangeBar>

      {data && <>
        {/* KPIs */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))', gap: 10, marginBottom: 16 }}>
          <KpiCard icon={Package} label={t('reports.productsSold')} value={data.total_products} color="var(--primary)" />
          <KpiCard icon={DollarSign} label={t('reports.totalRevenue')} value={fmt(data.total_revenue)} color="var(--success)" />
          <KpiCard icon={TrendingUp} label={t('reports.totalProfit')} value={fmt(data.total_profit)} color="var(--info)" />
        </div>

        {/* Charts */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div className="card" style={{ padding: 16 }}>
            <SectionTitle icon={BarChart2} color="var(--primary)">{t('reports.top10ByRevenue')}</SectionTitle>
            {top10.length > 0 && <ResponsiveContainer width="100%" height={220}><BarChart layout="vertical" data={top10}><XAxis type="number" tick={{ fontSize: 9 }} tickFormatter={v => fmt(v)} /><YAxis type="category" dataKey="name" tick={{ fontSize: 9 }} width={110} /><Tooltip formatter={v => fmt(v)} /><Bar dataKey="total_revenue" fill="#6366f1" radius={[0, 4, 4, 0]} /></BarChart></ResponsiveContainer>}
          </div>
          <div className="card" style={{ padding: 16 }}>
            <SectionTitle icon={TrendingUp} color="var(--success)">{t('reports.profitMargins')}</SectionTitle>
            {top10.length > 0 && <ResponsiveContainer width="100%" height={220}><BarChart layout="vertical" data={top10}><XAxis type="number" tick={{ fontSize: 9 }} tickFormatter={v => `${v}%`} /><YAxis type="category" dataKey="name" tick={{ fontSize: 9 }} width={110} /><Tooltip formatter={v => `${v}%`} /><Bar dataKey="margin" fill="#22c55e" radius={[0, 4, 4, 0]} /></BarChart></ResponsiveContainer>}
          </div>
        </div>

        {/* Detail table with expandable UoM rows */}
        <div className="card" style={{ padding: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <SectionTitle icon={Package} color="var(--primary)">{t('reports.productSalesDetail')}</SectionTitle>
            <span style={{ fontSize: 11, color: 'var(--td)' }}>Click row to expand UoM breakdown</span>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: 'var(--s2)', borderBottom: '1px solid var(--border)' }}>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: 'var(--tm)', fontSize: 11, whiteSpace: 'nowrap' }}>#</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: 'var(--tm)', fontSize: 11 }}>{t('common.product')}</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: 'var(--tm)', fontSize: 11 }}>SKU</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: 'var(--tm)', fontSize: 11 }}>{t('reports.col.category')}</th>
                  <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 700, color: 'var(--primary)', fontSize: 11 }}>Base UoM</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, color: 'var(--tm)', fontSize: 11 }}>{t('reports.col.qtySold')}</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, color: 'var(--tm)', fontSize: 11 }}>POS</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, color: 'var(--tm)', fontSize: 11 }}>Invoice</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, color: 'var(--tm)', fontSize: 11 }}>{t('reports.revenue')}</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, color: 'var(--tm)', fontSize: 11 }}>{t('reports.col.profit')}</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, color: 'var(--tm)', fontSize: 11 }}>{t('reports.col.margin')}</th>
                  <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 700, color: 'var(--tm)', fontSize: 11 }}>UoM</th>
                </tr>
              </thead>
              <tbody>
                {(data.products || []).map((p, idx) => {
                  const isExpanded = expandedRows[p.id];
                  const hasUom = p.uom_breakdown && p.uom_breakdown.length > 0;
                  return (
                    <React.Fragment key={p.id}>
                      {/* Product row */}
                      <tr
                        onClick={() => hasUom && toggleRow(p.id)}
                        style={{ borderBottom: '1px solid var(--border)', background: idx % 2 === 0 ? 'var(--surface)' : 'var(--s2)', cursor: hasUom ? 'pointer' : 'default', transition: 'background .1s' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--pm)'}
                        onMouseLeave={e => e.currentTarget.style.background = idx % 2 === 0 ? 'var(--surface)' : 'var(--s2)'}
                      >
                        <td style={{ padding: '9px 10px', color: 'var(--td)', fontSize: 11 }}>{idx + 1}</td>
                        <td style={{ padding: '9px 10px', fontWeight: 700 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            {hasUom && (isExpanded ? <ChevronUp size={12} color="var(--primary)" /> : <ChevronDown size={12} color="var(--td)" />)}
                            {p.name}
                          </div>
                        </td>
                        <td style={{ padding: '9px 10px', fontFamily: 'monospace', color: 'var(--primary)', fontSize: 11 }}>{p.sku}</td>
                        <td style={{ padding: '9px 10px', color: 'var(--td)' }}>{p.category}</td>
                        <td style={{ padding: '9px 10px', textAlign: 'center' }}>
                          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: 'rgba(99,102,241,.12)', color: 'var(--primary)', border: '1px solid rgba(99,102,241,.2)' }}>{p.base_uom}</span>
                        </td>
                        <td style={{ padding: '9px 10px', textAlign: 'right', fontWeight: 700, fontSize: 13 }}>{p.total_qty}</td>
                        <td style={{ padding: '9px 10px', textAlign: 'right', color: 'var(--td)' }}>{p.pos_qty > 0 ? p.pos_qty : '—'}</td>
                        <td style={{ padding: '9px 10px', textAlign: 'right', color: 'var(--td)' }}>{p.inv_qty > 0 ? p.inv_qty : '—'}</td>
                        <td style={{ padding: '9px 10px', textAlign: 'right', fontWeight: 700 }}>{fmt(p.total_revenue)}</td>
                        <td style={{ padding: '9px 10px', textAlign: 'right', color: 'var(--success)', fontWeight: 600 }}>{fmt(p.profit)}</td>
                        <td style={{ padding: '9px 10px', textAlign: 'right' }}>
                          <span style={{ fontWeight: 700, color: p.margin > 30 ? 'var(--success)' : p.margin > 15 ? 'var(--warning)' : 'var(--danger)' }}>{p.margin}%</span>
                        </td>
                        <td style={{ padding: '9px 10px', textAlign: 'center' }}>
                          {hasUom && (
                            <span style={{ fontSize: 10, background: 'var(--s2)', border: '1px solid var(--border)', borderRadius: 12, padding: '2px 7px', color: 'var(--td)' }}>
                              {p.uom_breakdown.length} UoM
                            </span>
                          )}
                        </td>
                      </tr>

                      {/* Expanded UoM breakdown rows */}
                      {isExpanded && hasUom && (
                        <tr style={{ borderBottom: '2px solid var(--border)' }}>
                          <td colSpan={12} style={{ padding: 0 }}>
                            <div style={{ background: 'var(--s2)', borderLeft: '3px solid var(--primary)', margin: '0 0 2px 40px' }}>
                              {/* UoM sub-header */}
                              <div style={{ display: 'grid', gridTemplateColumns: '180px 80px 80px 80px 80px 90px 100px', gap: 0, padding: '6px 12px', borderBottom: '1px solid var(--border)', background: 'rgba(99,102,241,.06)' }}>
                                {['Unit of Measure', 'Conv ×', 'Total Qty', 'POS Qty', 'Inv Qty', 'Avg Price', 'Revenue'].map(h => (
                                  <div key={h} style={{ fontSize: 10, fontWeight: 700, color: 'var(--primary)', textAlign: h === 'Unit of Measure' ? 'left' : 'right', padding: '0 4px' }}>{h}</div>
                                ))}
                              </div>
                              {/* UoM rows */}
                              {p.uom_breakdown.map((u, ui) => (
                                <div key={ui} style={{ display: 'grid', gridTemplateColumns: '180px 80px 80px 80px 80px 90px 100px', gap: 0, padding: '8px 12px', borderBottom: ui < p.uom_breakdown.length - 1 ? '1px solid var(--border)' : 'none', alignItems: 'center', background: ui % 2 === 0 ? 'transparent' : 'rgba(255,255,255,.02)' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <span style={{ color: 'var(--td)', fontSize: 11 }}>└</span>
                                    {uomBadge(u.uom_abbr, u.conversion_factor)}
                                    <span style={{ fontSize: 12, color: 'var(--text)' }}>{u.uom_name}</span>
                                  </div>
                                  <div style={{ textAlign: 'right', fontSize: 11, color: 'var(--td)', padding: '0 4px' }}>
                                    {u.conversion_factor !== 1 ? <span style={{ fontWeight: 600, color: 'var(--warning)' }}>×{u.conversion_factor}</span> : <span style={{ color: 'var(--td)' }}>×1</span>}
                                  </div>
                                  <div style={{ textAlign: 'right', fontWeight: 700, fontSize: 13, padding: '0 4px' }}>{u.total_qty}</div>
                                  <div style={{ textAlign: 'right', fontSize: 11, color: 'var(--td)', padding: '0 4px' }}>{u.pos_qty > 0 ? u.pos_qty : '—'}</div>
                                  <div style={{ textAlign: 'right', fontSize: 11, color: 'var(--td)', padding: '0 4px' }}>{u.inv_qty > 0 ? u.inv_qty : '—'}</div>
                                  <div style={{ textAlign: 'right', fontSize: 12, color: 'var(--text)', padding: '0 4px' }}>{fmt(u.avg_unit_price)}</div>
                                  <div style={{ textAlign: 'right', fontWeight: 600, color: 'var(--success)', padding: '0 4px' }}>{fmt(u.total_revenue)}</div>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {(!data.products || data.products.length === 0) && (
                  <tr><td colSpan={12} style={{ padding: 32, textAlign: 'center', color: 'var(--td)' }}>{t('reports.noData')}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </>}
    </div>
  );
}
// ════════════════════════════════════════════════════════════════
// 7. STOCK REPORT
// ════════════════════════════════════════════════════════════════
function StockReportTab({ branches, isOwner }) {
  const { t } = useLang();
  const { hasPermission } = useAuth();
  const canSeeCost = hasPermission('cost_price.view');
  const [bid, setBid] = useState('');
  const [filter, setFilter] = useState('all');
  const [df, setDf] = useState('');
  const [dt, setDt] = useState('');
  const [data, setData] = useState(null);
  const [ld, setLd] = useState(false);

  const run = async () => {
    setLd(true);
    try {
      const p = {
        ...(isOwner && bid ? { branch_id: bid } : {}),
        low_stock: filter === 'low' ? 'true' : undefined,
        out_of_stock: filter === 'out' ? 'true' : undefined,
        ...(df ? { date_from: df } : {}),
        ...(dt ? { date_to: dt } : {}),
      };
      const r = await reportAPI.stockSummary(p);
      setData(r.data);
    } catch { toast.error('Failed') } finally { setLd(false) }
  };
  useEffect(() => { run() }, []);

  // UoM breakdown display component
  const UomDisplay = ({ item }) => {
    const { uom_breakdown = [], quantity, base_uom, stock_display } = item;
    if (!uom_breakdown || uom_breakdown.length === 0) {
      return <span style={{ fontWeight: 700 }}>{quantity} <span style={{ fontSize: 10, color: 'var(--td)' }}>{base_uom}</span></span>;
    }
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Main display string */}
        <div style={{ fontWeight: 700, fontSize: 13 }}>
          {uom_breakdown.map((u, i) => (
            <span key={i}>
              {i > 0 && <span style={{ color: 'var(--td)', margin: '0 4px', fontWeight: 400 }}>+</span>}
              <span style={{ color: u.factor > 1 ? 'var(--primary)' : 'var(--text)' }}>{u.count}</span>
              {' '}
              <span style={{
                fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 12,
                background: u.factor > 1 ? 'rgba(99,102,241,.12)' : 'var(--s2)',
                color: u.factor > 1 ? 'var(--primary)' : 'var(--td)',
                border: `1px solid ${u.factor > 1 ? 'rgba(99,102,241,.2)' : 'var(--border)'}`,
              }}>{u.uom_abbr}</span>
            </span>
          ))}
        </div>
        {/* Total base units in small text */}
        <div style={{ fontSize: 10, color: 'var(--td)' }}>= {quantity} {base_uom} {t('common.total')}</div>
      </div>
    );
  };

  const csvData = (data?.items || []).map(r => ({
    name: r.name, sku: r.sku, category: r.category, branch: r.branch,
    quantity: r.quantity, base_uom: r.base_uom, stock_display: r.stock_display,
    reorder_level: r.reorder_level, safety_stock: r.safety_stock,
    stock_value: r.stock_value, status: r.status,
  }));

  return (
    <div>
      {/* Filter bar */}
      <div style={{ background: 'var(--surface)', borderRadius: 12, padding: '12px 16px', border: '1px solid var(--border)', marginBottom: 16, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-end' }}>
        {/* Branch */}
        {isOwner && branches?.length > 0 && (
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--td)', marginBottom: 4, letterSpacing: .5 }}>{t('common.branch').toUpperCase()}</div>
            <select value={bid} onChange={e => setBid(e.target.value)} style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--s2)', color: 'var(--text)', fontSize: 12, minWidth: 150 }}>
              <option value="">{t('reports.allBranches')}</option>
              {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
        )}
        {/* Date from */}
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--td)', marginBottom: 4, letterSpacing: .5 }}>{t('common.fromDate').toUpperCase()}</div>
          <input type="date" value={df} onChange={e => setDf(e.target.value)} style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--s2)', color: 'var(--text)', fontSize: 12 }} />
        </div>
        {/* Date to */}
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--td)', marginBottom: 4, letterSpacing: .5 }}>{t('common.toDate').toUpperCase()}</div>
          <input type="date" value={dt} onChange={e => setDt(e.target.value)} style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--s2)', color: 'var(--text)', fontSize: 12 }} />
        </div>
        {/* Stock filter pills */}
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--td)', marginBottom: 4, letterSpacing: .5 }}>STATUS</div>
          <div style={{ display: 'flex', gap: 4 }}>
            {[['all', t('reports.filter.all')], ['low', t('reports.filter.lowStock')], ['out', t('reports.filter.outOfStock')]].map(([v, l]) => (
              <button key={v} className="btn btn-sm" style={{ fontSize: 11, background: filter === v ? 'var(--primary)' : 'var(--s2)', color: filter === v ? '#fff' : 'var(--td)', border: `1px solid ${filter === v ? 'var(--primary)' : 'var(--border)'}`, padding: '5px 12px' }} onClick={() => setFilter(v)}>{l}</button>
            ))}
          </div>
        </div>
        {/* Run button */}
        <button className="btn btn-primary btn-sm" onClick={run} disabled={ld} style={{ gap: 5, fontSize: 12, height: 34, alignSelf: 'flex-end' }}>
          {ld ? <RefreshCw size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <Filter size={12} />} {t('reports.runReport')}
        </button>
        {data && <div style={{ alignSelf: 'flex-end' }}><ExportCSV data={csvData} columns={[{ key: 'name', label: 'Product' }, { key: 'sku', label: 'SKU' }, { key: 'category', label: 'Category' }, { key: 'branch', label: 'Branch' }, { key: 'quantity', label: 'Qty (base)' }, { key: 'base_uom', label: 'Base UoM' }, { key: 'stock_display', label: 'Stock Display' }, { key: 'reorder_level', label: 'Reorder' }, { key: 'safety_stock', label: 'Safety' }, { key: 'stock_value', label: 'Value' }, { key: 'status', label: 'Status' }]} filename="stock-report" /></div>}
      </div>

      {data && <>
        {/* KPIs */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))', gap: 10, marginBottom: 16 }}>
          <KpiCard icon={Package} label={t('reports.skusTracked')} value={data.total_items} color="var(--primary)" />
          {canSeeCost && <KpiCard icon={DollarSign} label={t('reports.stockValue')} value={fmt(data.total_value)} color="var(--success)" />}
          <KpiCard icon={AlertTriangle} label={t('reports.lowStock')} value={data.low_stock} color="var(--warning)" />
          <KpiCard icon={TrendingDown} label={t('reports.outOfStock')} value={data.out_of_stock} color="var(--danger)" />
        </div>

        {/* Stock table with UoM display */}
        <div className="card" style={{ padding: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <SectionTitle icon={Warehouse} color="var(--primary)">{t('reports.stockLevels')}</SectionTitle>
            <div style={{ fontSize: 11, color: 'var(--td)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'rgba(99,102,241,.4)', display: 'inline-block' }} />
              Alt UoM &nbsp;
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--s2)', border: '1px solid var(--border)', display: 'inline-block' }} />
              Base UoM
            </div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: 'var(--s2)', borderBottom: '1px solid var(--border)' }}>
                  {[t('common.product'), 'SKU', t('reports.col.category'), t('common.branch'), t('inventory.col.onHand'), 'Base', t('reports.col.reorder'), t('inventory.col.available'), ...(canSeeCost?[t('reports.col.value')]:[]), t('common.status')].map(h => (
                    <th key={h} style={{ padding: '8px 10px', textAlign: ['Base', t('reports.col.reorder'), t('inventory.col.available'), t('reports.col.value')].includes(h) ? 'right' : 'left', fontWeight: 700, color: 'var(--tm)', fontSize: 11, whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(data.items || []).map((r, idx) => (
                  <tr key={r.id} style={{ borderBottom: '1px solid var(--border)', background: idx % 2 === 0 ? 'var(--surface)' : 'var(--s2)' }}>
                    <td style={{ padding: '10px 10px', fontWeight: 700 }}>{r.name}</td>
                    <td style={{ padding: '10px 10px', fontFamily: 'monospace', color: 'var(--primary)', fontSize: 11 }}>{r.sku}</td>
                    <td style={{ padding: '10px 10px', color: 'var(--td)' }}>{r.category}</td>
                    <td style={{ padding: '10px 10px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <span style={{ fontSize: 11 }}>{r.branch}</span>
                        {r.branch_code && <span style={{ fontSize: 9, fontFamily: 'monospace', background: 'var(--pm)', color: 'var(--primary)', padding: '1px 5px', borderRadius: 4 }}>{r.branch_code}</span>}
                      </div>
                    </td>
                    {/* UoM-converted quantity display */}
                    <td style={{ padding: '10px 10px', minWidth: 160 }}>
                      <UomDisplay item={r} />
                    </td>
                    {/* Raw base qty */}
                    <td style={{ padding: '10px 10px', textAlign: 'right', fontWeight: 600, color: r.quantity <= 0 ? 'var(--danger)' : r.quantity <= r.reorder_level ? 'var(--warning)' : 'var(--success)' }}>
                      {r.quantity}
                      <div style={{ fontSize: 9, color: 'var(--td)', fontWeight: 400 }}>{r.base_uom}</div>
                    </td>
                    <td style={{ padding: '10px 10px', textAlign: 'right', color: 'var(--td)' }}>{r.reorder_level}</td>
                    <td style={{ padding: '10px 10px', textAlign: 'right', color: 'var(--td)' }}>{r.safety_stock}</td>
                    {canSeeCost && <td style={{ padding: '10px 10px', textAlign: 'right', fontWeight: 600 }}>{fmt(r.stock_value)}</td>}
                    <td style={{ padding: '10px 10px' }}>
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 9px', borderRadius: 20, background: r.status === 'out_of_stock' ? '#fee2e2' : r.status === 'low_stock' ? '#fef3c7' : '#dcfce7', color: r.status === 'out_of_stock' ? '#dc2626' : r.status === 'low_stock' ? '#d97706' : '#16a34a' }}>
                        {r.status === 'out_of_stock' ? t('reports.outOfStock') : r.status === 'low_stock' ? t('reports.lowStock') : 'OK'}
                      </span>
                    </td>
                  </tr>
                ))}
                {(!data.items || data.items.length === 0) && (
                  <tr><td colSpan={10} style={{ padding: 32, textAlign: 'center', color: 'var(--td)' }}>{t('reports.noData')}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </>}
    </div>
  );
}
// ════════════════════════════════════════════════════════════════
// 8. PAYMENT FINANCE REPORT
// ════════════════════════════════════════════════════════════════
function PaymentReportTab({ branches, isOwner }) {
  const { t } = useLang();
  const [df, setDf] = useState(monthStart()); const [dt, setDt] = useState(today()); const [bid, setBid] = useState('');
  const [data, setData] = useState(null); const [ld, setLd] = useState(false);
  const run = async () => { setLd(true); try { const r = await reportAPI.paymentSummary({ date_from: df, date_to: dt, ...(isOwner && bid ? { branch_id: bid } : {}) }); setData(r.data); } catch { toast.error('Failed') } finally { setLd(false) } };
  useEffect(() => { run() }, []);
  return (
    <div>
      <DateRangeBar dateFrom={df} dateTo={dt} onFrom={setDf} onTo={setDt} onRun={run} loading={ld} branches={branches} branchId={bid} onBranch={setBid} isOwner={isOwner}>
        {data && <ExportCSV data={data.payments} columns={[{ key: 'number', label: 'Payment #' }, { key: 'date', label: 'Date' }, { key: 'method', label: 'Method' }, { key: 'customer', label: 'Customer' }, { key: 'branch', label: 'Branch' }, { key: 'invoice', label: 'Invoice' }, { key: 'amount', label: 'Amount (USD)' }, { key: 'original_amount', label: 'Orig Amount' }, { key: 'original_currency', label: 'Currency' }]} filename="payments" />}
      </DateRangeBar>
      {data && <>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))', gap: 10, marginBottom: 16 }}>
          <KpiCard icon={CreditCard} label={t('reports.payments')} value={data.summary?.total_payments} color="var(--primary)" />
          <KpiCard icon={DollarSign} label={t('reports.usdCollected')} value={fmt(data.summary?.total_amount)} color="var(--success)" />
          {data.summary?.khr_collected > 0 && <KpiCard icon={DollarSign} label={t('reports.khrCollected')} value={`៛${Math.round(data.summary.khr_collected).toLocaleString()}`} color="var(--warning)" />}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div className="card" style={{ padding: 16 }}>
            <SectionTitle icon={CreditCard} color="var(--info)">By Payment Method</SectionTitle>
            {data.summary?.by_method?.length > 0 && <ResponsiveContainer width="100%" height={200}><PieChart><Pie data={data.summary.by_method} dataKey="amount" nameKey="name" cx="50%" cy="50%" outerRadius={80} paddingAngle={3}>{data.summary.by_method.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip formatter={v => fmt(v)} /><Legend /></PieChart></ResponsiveContainer>}
            <Table cols={[{ key: 'name', label: 'Method' }, { key: 'count', label: 'Txns', right: true }, { key: 'amount', label: 'Amount', right: true, bold: true, render: r => fmt(r.amount) }]} data={data.summary?.by_method} />
          </div>
          <div className="card" style={{ padding: 16 }}>
            <SectionTitle icon={TrendingUp} color="var(--success)">{t('reports.dailyCashFlow')}</SectionTitle>
            {data.daily?.length > 0 && <ResponsiveContainer width="100%" height={200}><LineChart data={data.daily}><XAxis dataKey="date" tick={{ fontSize: 9 }} /><YAxis tick={{ fontSize: 9 }} /><Tooltip formatter={v => fmt(v)} /><Line type="monotone" dataKey="amount" stroke="#22c55e" dot={false} /></LineChart></ResponsiveContainer>}
          </div>
        </div>
        <div className="card" style={{ padding: 16 }}>
          <SectionTitle icon={CreditCard} color="var(--primary)">{t('reports.paymentDetails')}</SectionTitle>
          <Table cols={[{ key: 'number', label: 'Payment #', render: r => <span style={{ fontFamily: 'monospace', color: 'var(--primary)', fontWeight: 700 }}>{r.number}</span> }, { key: 'date', label: 'Date', render: r => fmtDate(r.date) }, { key: 'customer', label: 'Customer' }, { key: 'branch', label: 'Branch' }, { key: 'method', label: 'Method' }, { key: 'invoice', label: 'Invoice', render: r => <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{r.invoice}</span> }, { key: 'amount', label: 'USD', right: true, bold: true, render: r => fmt(r.amount) }, { key: 'original_amount', label: 'Orig', right: true, render: r => r.original_currency === 'KHR' ? `៛${Math.round(r.original_amount).toLocaleString()}` : fmt(r.original_amount) }]} data={data.payments} />
        </div>
      </>}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// 9. COMMISSION REPORT
// ════════════════════════════════════════════════════════════════
function CommissionReportTab({ branches, isOwner, myBranchId }) {
  const { hasPermission } = useAuth();
  const canSeeCost = hasPermission('cost_price.view');
  const canManageAll = isOwner || hasPermission('item_commissions.manage_all');
  const viewOwnOnly = !hasPermission('item_commissions.view') && !hasPermission('item_commissions.manage') && hasPermission('item_commissions.view_own');

  const [data, setData] = useState(null);
  const [ld, setLd] = useState(false);
  const [dateFrom, setDateFrom] = useState(monthStart());
  const [dateTo, setDateTo] = useState(today());
  const [branchId, setBranchId] = useState('');
  const [channelFilter, setChannelFilter] = useState('');
  const [view, setView] = useState('rep');
  const [reps, setReps] = useState([]);
  const [assigningRep, setAssigningRep] = useState(null);
  const [assignRepId, setAssignRepId] = useState('');

  useEffect(() => {
    userAPI.getAll({ limit: 200 }).then(r => setReps((r.data.data || r.data).filter(u => u.is_active))).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLd(true);
    try {
      const p = { date_from: dateFrom, date_to: dateTo };
      if (branchId) p.branch_id = branchId;
      if (channelFilter) p.reference_type = channelFilter;
      const r = await itemCommissionAPI.getReport(p);
      setData(r.data);
    } catch { toast.error('Failed to load commission report'); }
    finally { setLd(false); }
  }, [dateFrom, dateTo, branchId, channelFilter]);

  useEffect(() => { load(); }, []);

  const doAssignRep = async (row) => {
    if (!assignRepId) return toast.error('Select a rep');
    try {
      await itemCommissionAPI.assignRep({ reference_type: row.reference_type, reference_id: row.reference_id, sales_rep_id: parseInt(assignRepId) });
      toast.success('Rep assigned'); setAssigningRep(null); setAssignRepId(''); load();
    } catch { toast.error('Failed'); }
  };

  const selectStyle = { padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--s2)', color: 'var(--text)', fontSize: 12 };
  const TH = ({ children, right }) => <th style={{ padding: '8px 10px', textAlign: right ? 'right' : 'left', fontSize: 10, fontWeight: 700, color: 'var(--td)', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{children}</th>;
  const TD = ({ children, right, bold, color }) => <td style={{ padding: '8px 10px', textAlign: right ? 'right' : 'left', fontWeight: bold ? 700 : 400, color: color || 'var(--text)' }}>{children}</td>;

  return (
    <div>
      {viewOwnOnly && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.25)', borderRadius: 8, marginBottom: 12, fontSize: 12, color: 'var(--primary)' }}>
          <Users size={14} /> <strong>Viewing your own commissions only.</strong>
        </div>
      )}
      <DateRangeBar dateFrom={dateFrom} dateTo={dateTo} onFrom={setDateFrom} onTo={setDateTo}
        onRun={load} loading={ld} branches={branches} branchId={branchId} onBranch={setBranchId} isOwner={canManageAll}>
        <select value={channelFilter} onChange={e => setChannelFilter(e.target.value)} style={selectStyle}>
          <option value="">All Channels</option>
          {[['invoice','Invoice'],['pos_transaction','POS'],['sale_order','Sale Order'],['manual','Manual'],['emenu','E-Menu']].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </DateRangeBar>

      {data && <>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(140px,1fr))', gap: 10, marginBottom: 16 }}>
          <KpiCard icon={FileText} label="Total Sales" value={data.totals.count} color="var(--primary)" />
          {canSeeCost && <KpiCard icon={TrendingUp} label="Net Profit" value={`$${fmtNum(data.totals.net_profit)}`} color="var(--success)" />}
          <KpiCard icon={Building2} label="Business $" value={`$${fmtNum(data.totals.business_amount)}`} color="var(--accent)" />
          <KpiCard icon={Building2} label="Branch $" value={`$${fmtNum(data.totals.branch_amount)}`} color="var(--primary)" />
          <KpiCard icon={Users} label="Rep Commissions" value={`$${fmtNum(data.totals.rep_amount)}`} color="var(--warning)" />
          {(data.unassigned_count||0) > 0 && <div onClick={() => setView('unassigned')} style={{cursor:'pointer'}}><KpiCard icon={AlertTriangle} label="Unassigned Rep" value={data.unassigned_count} color="var(--danger)" sub="Click to assign" /></div>}
        </div>

        <div style={{ display: 'flex', gap: 4, marginBottom: 14, borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
          {[['rep','By Sales Rep',Users],['product','By Product',Package],['day','By Day',BarChart2],['month','By Month',FileText],['channel','By Channel',Tag],
            ...(data.unassigned_count>0?[['unassigned',`Unassigned (${data.unassigned_count})`,AlertTriangle]]:[])
          ].map(([k,label,Icon]) => (
            <button key={k} onClick={() => setView(k)}
              style={{ display:'flex',alignItems:'center',gap:5,padding:'8px 14px',fontSize:12,fontWeight:600,border:'none',borderBottom:`2px solid ${view===k?'var(--primary)':'transparent'}`,background:'transparent',cursor:'pointer',color:view===k?'var(--primary)':k==='unassigned'?'var(--danger)':'var(--td)',marginBottom:-1 }}>
              <Icon size={13}/>{label}
            </button>
          ))}
        </div>

        {view==='rep' && <>
          {data.by_rep.length > 0 && <div style={{marginBottom:12}}>
            <ResponsiveContainer width="100%" height={Math.max(60,data.by_rep.length*44)}>
              <BarChart layout="vertical" data={data.by_rep}>
                <XAxis type="number" tick={{fontSize:9}} tickFormatter={v=>`$${v.toFixed(0)}`}/>
                <YAxis type="category" dataKey="name" tick={{fontSize:10}} width={130}/>
                <Tooltip formatter={v=>`$${v.toFixed(2)}`}/>
                <Bar dataKey="rep_amount" fill="#f59e0b" radius={[0,4,4,0]} name="Commission"/>
                {canSeeCost && <Bar dataKey="net_profit" fill="#22c55e" radius={[0,4,4,0]} name="Net Profit"/>}
              </BarChart>
            </ResponsiveContainer>
          </div>}
          <Table cols={[{key:'name',label:'Sales Rep',bold:true},{key:'sales',label:'Sales',right:true},...(canSeeCost?[{key:'net_profit',label:'Net Profit',right:true,render:r=><span style={{color:'var(--success)',fontWeight:700}}>{fmt(r.net_profit)}</span>}]:[]),{key:'rep_amount',label:'Rep Commission',right:true,bold:true,render:r=><span style={{color:'var(--warning)',fontWeight:800}}>{fmt(r.rep_amount)}</span>}]} data={data.by_rep} emptyMsg="No commission data"/>
        </>}

        {view==='product' && <div style={{border:'1px solid var(--border)',borderRadius:10,overflow:'hidden'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
            <thead><tr style={{background:'var(--s2)'}}><TH>Product</TH><TH>SKU</TH><TH right>Qty</TH>{canSeeCost&&<TH right>Net Profit</TH>}<TH right>Biz $</TH><TH right>Branch $</TH><TH right>Rep $</TH></tr></thead>
            <tbody>{data.by_product.map((r,i)=><tr key={r.id} style={{borderBottom:'1px solid var(--border)',background:i%2===0?'var(--surface)':'var(--s2)'}}><TD bold>{r.name}</TD><TD><span style={{fontFamily:'monospace',fontSize:11,color:'var(--primary)'}}>{r.sku}</span></TD><TD right>{r.qty}</TD>{canSeeCost&&<TD right color="var(--success)">{fmt(r.net_profit)}</TD>}<TD right color="var(--accent)">{fmt(r.business_amount)}</TD><TD right color="var(--primary)">{fmt(r.branch_amount)}</TD><TD right color="var(--warning)">{fmt(r.rep_amount)}</TD></tr>)}
            {!data.by_product.length&&<tr><td colSpan={7} style={{textAlign:'center',padding:20,color:'var(--td)'}}>No data</td></tr>}</tbody>
          </table>
        </div>}

        {['day','month'].includes(view) && <div style={{border:'1px solid var(--border)',borderRadius:10,overflow:'hidden'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
            <thead><tr style={{background:'var(--s2)'}}><TH>{view==='day'?'Date':'Month'}</TH><TH right>Sales</TH>{canSeeCost&&<TH right>Net Profit</TH>}<TH right>Biz $</TH><TH right>Branch $</TH><TH right>Rep $</TH></tr></thead>
            <tbody>{(view==='day'?data.by_day:data.by_month||[]).map((r,i)=><tr key={r.date||r.month} style={{borderBottom:'1px solid var(--border)',background:i%2===0?'var(--surface)':'var(--s2)'}}><TD bold>{view==='day'?fmtDate(r.date):r.month}</TD><TD right>{r.sales}</TD>{canSeeCost&&<TD right color="var(--success)">{fmt(r.net_profit)}</TD>}<TD right color="var(--accent)">{fmt(r.business_amount)}</TD><TD right color="var(--primary)">{fmt(r.branch_amount)}</TD><TD right color="var(--warning)">{fmt(r.rep_amount)}</TD></tr>)}
            {!(view==='day'?data.by_day:data.by_month||[]).length&&<tr><td colSpan={6} style={{textAlign:'center',padding:20,color:'var(--td)'}}>No data</td></tr>}</tbody>
          </table>
        </div>}

        {view==='channel' && <div style={{border:'1px solid var(--border)',borderRadius:10,overflow:'hidden'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
            <thead><tr style={{background:'var(--s2)'}}><TH>Channel</TH><TH right>Sales</TH>{canSeeCost&&<TH right>Net Profit</TH>}<TH right>Biz $</TH><TH right>Branch $</TH><TH right>Rep $</TH></tr></thead>
            <tbody>{(data.by_channel||[]).map((r,i)=><tr key={r.channel} style={{borderBottom:'1px solid var(--border)',background:i%2===0?'var(--surface)':'var(--s2)'}}><TD bold>{r.label||r.channel}</TD><TD right>{r.sales}</TD>{canSeeCost&&<TD right color="var(--success)">{fmt(r.net_profit)}</TD>}<TD right color="var(--accent)">{fmt(r.business_amount)}</TD><TD right color="var(--primary)">{fmt(r.branch_amount)}</TD><TD right color="var(--warning)">{fmt(r.rep_amount)}</TD></tr>)}
            {!(data.by_channel||[]).length&&<tr><td colSpan={6} style={{textAlign:'center',padding:20,color:'var(--td)'}}>No data</td></tr>}</tbody>
          </table>
        </div>}

        {view==='unassigned' && <div>
          <div style={{padding:'10px 14px',background:'rgba(255,68,68,0.08)',border:'1px solid rgba(255,68,68,0.2)',borderRadius:8,marginBottom:12,fontSize:12,color:'var(--danger)'}}>
            <strong>⚠ {data.unassigned_count} record(s)</strong> have no sales rep assigned.
          </div>
          <div style={{border:'1px solid var(--border)',borderRadius:10,overflow:'hidden'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
              <thead><tr style={{background:'var(--s2)'}}><TH>Date</TH><TH>Product</TH><TH>Channel</TH>{canSeeCost&&<TH right>Net $</TH>}<TH right>Rep $</TH><TH>Assign Rep</TH></tr></thead>
              <tbody>{(data.unassigned||[]).map((r,i)=>(
                <tr key={r.id} style={{borderBottom:'1px solid var(--border)',background:i%2===0?'var(--surface)':'var(--s2)'}}>
                  <td style={{padding:'7px 10px',whiteSpace:'nowrap'}}>{fmtDate(r.sale_date)}</td>
                  <td style={{padding:'7px 10px',fontWeight:600}}>{r.product}</td>
                  <td style={{padding:'7px 10px'}}><span style={{fontSize:10,padding:'2px 7px',borderRadius:10,background:'var(--pm)',color:'var(--primary)',fontWeight:700}}>{r.channel}</span></td>
                  {canSeeCost&&<td style={{padding:'7px 10px',textAlign:'right',color:'var(--success)',fontWeight:700}}>{fmt(r.net_profit)}</td>}
                  <td style={{padding:'7px 10px',textAlign:'right',color:'var(--warning)',fontWeight:700}}>{fmt(r.rep_amount)}</td>
                  <td style={{padding:'7px 10px'}}>
                    {assigningRep===r.id?(
                      <div style={{display:'flex',gap:6,alignItems:'center'}}>
                        <select value={assignRepId} onChange={e=>setAssignRepId(e.target.value)} style={{padding:'4px 8px',borderRadius:6,border:'1px solid var(--border)',background:'var(--s2)',fontSize:11}}>
                          <option value="">— Rep —</option>
                          {reps.map(u=><option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
                        </select>
                        <button className="btn btn-primary btn-sm" onClick={()=>doAssignRep(r)} style={{fontSize:10,padding:'4px 8px'}}>Save</button>
                        <button className="btn btn-ghost btn-sm" onClick={()=>{setAssigningRep(null);setAssignRepId('');}} style={{fontSize:10}}>✕</button>
                      </div>
                    ):(
                      <button className="btn btn-ghost btn-sm" onClick={()=>{setAssigningRep(r.id);setAssignRepId('');}} style={{fontSize:11,color:'var(--primary)',gap:4}}>
                        <Users size={11}/> Assign
                      </button>
                    )}
                  </td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </div>}
      </>}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// MAIN REPORTS PAGE
// ════════════════════════════════════════════════════════════════


export default function ReportsPage() {
  const { hasPermission, isOwner, myBranchId } = useAuth();
  const { t } = useLang();
  const [tab, setTab] = useState('pos-summary');
  const [branches, setBranches] = useState([]);
  const REPORT_TABS = [
    { id: 'pos-summary',     label: t('reports.tabs.posSummary'),    icon: ShoppingCart,   perms: ['reports.pos_summary',    'reports.sales'],     group: 'POS' },
    { id: 'pos-sessions',    label: t('reports.tabs.posSessions'),   icon: Clock,          perms: ['reports.pos_sessions',   'reports.sales'],     group: 'POS' },
    { id: 'cash-register',   label: t('reports.tabs.cashRegister'),  icon: CreditCard,     perms: ['reports.cash_register',  'reports.sales'],     group: 'POS' },
    { id: 'sales-orders',    label: t('reports.tabs.salesOrders'),   icon: FileText,       perms: ['reports.orders',         'reports.sales'],     group: 'Sales' },
    { id: 'sales-invoices',  label: t('reports.tabs.salesInvoices'), icon: FileText,       perms: ['reports.invoices',       'reports.financial'], group: 'Sales' },
    { id: 'customers',       label: t('reports.tabs.customers'),     icon: Users,          perms: ['reports.customers',      'reports.sales'],     group: 'Customers' },
    { id: 'product-sales',   label: t('reports.tabs.productSales'),  icon: Package,        perms: ['reports.product_sales',  'reports.inventory'], group: 'Inventory' },
    { id: 'stock',           label: t('reports.tabs.stock'),         icon: Warehouse,      perms: ['reports.stock',          'reports.inventory'], group: 'Inventory' },
    { id: 'vendors',         label: t('reports.tabs.vendors'),       icon: Building2,      perms: ['reports.vendors',        'reports.inventory'], group: 'Inventory' },
    { id: 'purchase-orders', label: 'Purchase Orders',               icon: ClipboardList,  perms: ['reports.purchase_orders','reports.inventory'], group: 'Purchasing' },
    { id: 'stock-transfers', label: 'Stock Transfers',               icon: ArrowLeftRight, perms: ['reports.stock_transfers','reports.inventory'], group: 'Purchasing' },
    { id: 'vendor-payments', label: 'Vendor Payments',               icon: Truck,          perms: ['reports.vendor_payments','reports.financial'], group: 'Purchasing' },
    { id: 'payments',        label: t('reports.tabs.payments'),      icon: CreditCard,     perms: ['reports.payments',       'reports.financial'], group: 'Finance' },
    { id: 'commissions',     label: 'Item Commissions',              icon: Award,          perms: ['reports.commissions',    'reports.commission', 'item_commissions.view_own'], group: 'Finance' },
    { id: 'expenses',        label: 'Expenses',                      icon: TrendingDown,   perms: ['reports.expenses',       'expenses.view'],     group: 'Finance' },
  ];
  useEffect(() => { branchAPI.getAll({ limit: 100 }).then(r => setBranches(r.data.data || [])).catch(() => { }) }, []);

  const availableTabs = REPORT_TABS.filter(tab => isOwner || tab.perms.some(p => hasPermission(p)));
  if (!availableTabs.length) return <div style={{ padding: '60px 20px', textAlign: 'center', color: 'var(--td)' }}><Lock size={32} style={{ marginBottom: 12, opacity: .4 }} /><div>You don't have permission to view reports.</div></div>;

  // Ensure tab is valid
  const activeTab = availableTabs.find(t2 => t2.id === tab) ? tab : availableTabs[0].id;

  // Group tabs
  const groups = [...new Set(availableTabs.map(tb => tb.group))];

  const props = { branches, isOwner, myBranchId };

  return (
    <div>
      <PageHeader title={t('reports.title')} subtitle={t('reports.subtitle')} />

      {/* Tab bar grouped */}
      <div style={{ marginBottom: 20, borderBottom: '1px solid var(--border)', overflowX: 'auto', WebkitOverflowScrolling:'touch', scrollbarWidth:'none', msOverflowStyle:'none' }}>
        {groups.map(group => {
          const groupTabs = availableTabs.filter(t => t.group === group);
          return (
            <div key={group} style={{ display: 'inline-flex', flexDirection: 'column', marginRight: 2 }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--td)', letterSpacing: .6, textTransform: 'uppercase', padding: '4px 12px 2px', opacity: .7 }}>{t('reports.groups.' + group.toLowerCase()) || group}</div>
              <div style={{ display: 'flex' }}>
                {groupTabs.map(t => (
                  <button key={t.id} onClick={() => setTab(t.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 5, padding: '8px 14px', fontSize: 12, fontWeight: 600,
                      border: 'none', borderBottom: `2px solid ${activeTab === t.id ? 'var(--primary)' : 'transparent'}`,
                      background: 'transparent', cursor: 'pointer', color: activeTab === t.id ? 'var(--primary)' : 'var(--td)',
                      marginBottom: -1, transition: 'color .15s,border-color .15s', whiteSpace: 'nowrap'
                    }}>
                    <t.icon size={13} />{t.label}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Tab content */}
      {activeTab === 'pos-summary' && <PosSummaryTab      {...props} />}
      {activeTab === 'pos-sessions' && <PosSessionsTab     {...props} />}
      {activeTab === 'cash-register' && <CashRegisterTab    {...props} />}
      {activeTab === 'sales-orders' && <SalesOrdersTab     {...props} />}
      {activeTab === 'sales-invoices' && <SalesInvoicesTab   {...props} />}
      {activeTab === 'customers' && <CustomerReportTab  {...props} />}
      {activeTab === 'product-sales' && <ProductSalesTab    {...props} />}
      {activeTab === 'stock' && <StockReportTab     {...props} />}
      {activeTab === 'vendors' && <VendorReportTab    {...props} />}
      {activeTab === 'purchase-orders' && <PurchaseOrderReportTab {...props} />}
      {activeTab === 'stock-transfers' && <StockTransferReportTab {...props} />}
      {activeTab === 'vendor-payments' && <VendorPaymentReportTab {...props} />}
      {activeTab === 'payments'        && <PaymentReportTab   {...props} />}
      {activeTab === 'commissions'     && <CommissionReportTab{...props} />}
      {activeTab === 'expenses'        && <ExpenseReportTab   {...props} />}
    </div>
  );
}

// ── Cash Register (simple) ────────────────────────────────────
function CashRegisterTab({ branches, isOwner }) {
  const {t} = useLang();
  const [df, setDf] = useState(monthStart()); const [dt, setDt] = useState(today()); const [bid, setBid] = useState('');
  const [data, setData] = useState(null); const [ld, setLd] = useState(false);
  const run = async () => { setLd(true); try { const r = await reportAPI.cashRegister({ date_from: df, date_to: dt, ...(isOwner && bid ? { branch_id: bid } : {}) }); setData(r.data); } catch { toast.error('Failed') } finally { setLd(false) } };
  useEffect(() => { run() }, []);
  return (
    <div>
      <DateRangeBar dateFrom={df} dateTo={dt} onFrom={setDf} onTo={setDt} onRun={run} loading={ld} branches={branches} branchId={bid} onBranch={setBid} isOwner={isOwner}>
        {data && <ExportCSV data={data.entries} columns={[{ key: 'type', label: 'Type' }, { key: 'amount', label: 'Amount' }, { key: 'user', label: 'User' }, { key: 'branch', label: 'Branch' }, { key: 'notes', label: 'Notes' }, { key: 'created_at', label: 'Date', render: r => fmtDate(r.created_at) }]} filename="cash-register" />}
      </DateRangeBar>
      {data && <>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))', gap: 10, marginBottom: 16 }}>
          <KpiCard icon={TrendingUp} label={t('reports.cashIn')} value={fmt(data.total_cash_in)} color="var(--success)" />
          <KpiCard icon={TrendingDown} label={t('reports.cashOut')} value={fmt(data.total_cash_out)} color="var(--danger)" />
          <KpiCard icon={DollarSign} label={t('reports.net')} value={fmt(data.net)} color={data.net >= 0 ? 'var(--success)' : 'var(--danger)'} />
        </div>
        <div className="card" style={{ padding: 16 }}>
          <SectionTitle icon={CreditCard} color="var(--primary)">{t('reports.cashEntries')}</SectionTitle>
          <Table cols={[{ key: 'type', label: 'Type', render: r => <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 700, background: r.type === 'cash_in' ? '#dcfce7' : '#fee2e2', color: r.type === 'cash_in' ? '#16a34a' : '#dc2626' }}>{r.type}</span> }, { key: 'amount', label: 'Amount', right: true, bold: true, render: r => fmt(r.amount) }, { key: 'user', label: 'User' }, { key: 'branch', label: 'Branch' }, { key: 'notes', label: 'Notes' }, { key: 'created_at', label: 'Date', render: r => fmtDate(r.created_at) }]} data={data.entries} />
        </div>
      </>}
    </div>
  );
}

// ── Vendor Report ─────────────────────────────────────────────
// ════════════════════════════════════════════════════════════════
// PURCHASE ORDERS REPORT
// ════════════════════════════════════════════════════════════════
function PurchaseOrderReportTab({ branches, isOwner, myBranchId }) {
  const [data, setData] = useState(null);
  const [ld, setLd] = useState(false);
  const [dateFrom, setDateFrom] = useState(monthStart());
  const [dateTo, setDateTo] = useState(today());
  const [branchId, setBranchId] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('');
  const [expandedPO, setExpandedPO] = useState(null);

  const run = useCallback(async () => {
    setLd(true);
    try {
      const p = { date_from: dateFrom, date_to: dateTo };
      if (branchId) p.branch_id = branchId;
      if (statusFilter) p.status = statusFilter;
      if (paymentFilter) p.payment_status = paymentFilter;
      const r = await reportAPI.purchaseOrderReport(p);
      setData(r.data);
    } catch { toast.error('Failed to load PO report'); }
    finally { setLd(false); }
  }, [dateFrom, dateTo, branchId, statusFilter, paymentFilter]);

  useEffect(() => { run(); }, []);

  const STATUS_COLORS = { draft:'#64748b',sent:'#2563eb',confirmed:'#7c3aed',partial:'#d97706',received:'#16a34a',cancelled:'#dc2626' };
  const PAY_COLORS   = { unpaid:'#dc2626', partial_paid:'#d97706', paid:'#16a34a' };
  const PAY_LABELS   = { unpaid:'Unpaid', partial_paid:'Part Paid', paid:'Paid' };

  const selectStyle = { padding:'7px 10px',borderRadius:8,border:'1px solid var(--border)',background:'var(--s2)',color:'var(--text)',fontSize:12 };

  return (
    <div>
      <DateRangeBar dateFrom={dateFrom} dateTo={dateTo} onFrom={setDateFrom} onTo={setDateTo}
        onRun={run} loading={ld} branches={branches} branchId={branchId} onBranch={setBranchId} isOwner={isOwner}>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={selectStyle}>
          <option value="">All Statuses</option>
          {['draft','sent','confirmed','partial','received','cancelled'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={paymentFilter} onChange={e => setPaymentFilter(e.target.value)} style={selectStyle}>
          <option value="">All Payments</option>
          {['unpaid','partial_paid','paid'].map(s => <option key={s} value={s}>{PAY_LABELS[s]}</option>)}
        </select>
        {data && <ExportCSV data={data.orders} filename="purchase-orders-report"
          columns={[{key:'po_number',label:'PO#'},{key:'vendor',label:'Vendor'},{key:'branch',label:'Branch'},{key:'order_date',label:'Date'},{key:'status',label:'Status'},{key:'payment_status',label:'Payment'},{key:'paid_amount',label:'Paid',render:r=>fmtNum(r.paid_amount)},{key:'total_value',label:'Total',render:r=>fmtNum(r.total_value)},{key:'received_value',label:'Received',render:r=>fmtNum(r.received_value)}]}/>}
      </DateRangeBar>

      {data && <>
        <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))',gap:10,marginBottom:16 }}>
          <KpiCard icon={ClipboardList} label="Total Orders" value={data.total_orders} color="var(--primary)"/>
          <KpiCard icon={DollarSign} label="Total Value" value={`$${fmtNum(data.total_value)}`} color="var(--accent)"/>
          <KpiCard icon={CheckCircle} label="Received Value" value={`$${fmtNum(data.total_received_value)}`} color="var(--success)"/>
          <KpiCard icon={AlertTriangle} label="Pending Value" value={`$${fmtNum(data.pending_value)}`} color="var(--warning)"/>
          <KpiCard icon={CheckCircle} label="Paid POs" value={data.by_payment_status?.paid||0} color="var(--success)" small/>
          <KpiCard icon={Clock} label="Unpaid POs" value={data.by_payment_status?.unpaid||0} color="var(--danger)" small/>
        </div>

        <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:14,marginBottom:14 }}>
          <div className="card" style={{ padding:16 }}>
            <SectionTitle icon={ClipboardList} color="var(--primary)">Orders by Status</SectionTitle>
            <div style={{ display:'flex',flexWrap:'wrap',gap:8 }}>
              {Object.entries(data.by_status).map(([s,count]) => (
                <div key={s} style={{ flex:1,minWidth:90,textAlign:'center',padding:'10px 8px',borderRadius:8,background:'var(--s2)',border:'1px solid var(--border)' }}>
                  <div style={{ fontSize:20,fontWeight:800,color:STATUS_COLORS[s]||'var(--text)' }}>{count}</div>
                  <div style={{ fontSize:10,color:'var(--td)',textTransform:'capitalize',marginTop:2 }}>{s}</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop:12,display:'flex',gap:8,flexWrap:'wrap' }}>
              {Object.entries(data.by_payment_status||{}).map(([s,count]) => (
                <div key={s} style={{ flex:1,minWidth:90,textAlign:'center',padding:'8px',borderRadius:8,background:'var(--s2)',border:'1px solid var(--border)' }}>
                  <div style={{ fontSize:16,fontWeight:800,color:PAY_COLORS[s]||'var(--text)' }}>{count}</div>
                  <div style={{ fontSize:10,color:'var(--td)',marginTop:1 }}>{PAY_LABELS[s]||s}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="card" style={{ padding:16 }}>
            <SectionTitle icon={Truck} color="var(--accent)">Top Vendors by Value</SectionTitle>
            <Table cols={[
              {key:'name',label:'Vendor',bold:true},
              {key:'orders',label:'Orders',right:true},
              {key:'value',label:'Total Value',right:true,bold:true,render:r=>fmt(r.value)},
              {key:'received',label:'Received',right:true,render:r=>fmt(r.received),color:r=>r.received>=r.value?'var(--success)':'var(--warning)'},
            ]} data={data.by_vendor.slice(0,6)}/>
          </div>
        </div>

        {/* Orders with expandable items */}
        <div className="card" style={{ padding:16 }}>
          <SectionTitle icon={FileText} color="var(--primary)">Purchase Order Details</SectionTitle>
          {data.orders.map((po,i) => (
            <div key={po.id} style={{ marginBottom:6,border:'1px solid var(--border)',borderRadius:8,overflow:'hidden' }}>
              <button onClick={() => setExpandedPO(expandedPO===po.id?null:po.id)}
                style={{ width:'100%',display:'flex',alignItems:'center',gap:10,padding:'9px 12px',background:i%2===0?'var(--surface)':'var(--s2)',border:'none',cursor:'pointer',textAlign:'left' }}>
                <span style={{ fontFamily:'monospace',fontSize:11,color:'var(--primary)',fontWeight:700,minWidth:90 }}>{po.po_number}</span>
                <span style={{ flex:1,fontSize:12,fontWeight:600 }}>{po.vendor}</span>
                <span style={{ fontSize:11,color:'var(--td)',minWidth:70 }}>{po.branch}</span>
                <span style={{ fontSize:11,color:'var(--td)',minWidth:80 }}>{fmtDate(po.order_date)}</span>
                <StatusBadge status={po.status}/>
                <span style={{ padding:'2px 7px',borderRadius:10,fontSize:10,fontWeight:700,background:PAY_COLORS[po.payment_status]+'22',color:PAY_COLORS[po.payment_status] }}>{PAY_LABELS[po.payment_status]||po.payment_status}</span>
                <span style={{ fontSize:12,fontWeight:700,minWidth:70,textAlign:'right' }}>{fmt(po.total_value)}</span>
                {expandedPO===po.id ? <ChevronUp size={13}/> : <ChevronDown size={13}/>}
              </button>
              {expandedPO===po.id && (
                <div style={{ padding:'4px 8px 8px' }}>
                  <div style={{ display:'flex',gap:12,padding:'6px 4px',marginBottom:4,fontSize:11,color:'var(--td)' }}>
                    <span>Paid: <strong style={{color:'var(--success)'}}>{fmt(po.paid_amount)}</strong></span>
                    <span>Balance: <strong style={{color:po.total_value-po.paid_amount>0?'var(--danger)':'var(--success)'}}>{fmt(po.total_value-(po.paid_amount||0))}</strong></span>
                    <span>Terms: <strong>{po.payment_terms}</strong></span>
                  </div>
                  <table style={{ width:'100%',borderCollapse:'collapse',fontSize:12 }}>
                    <thead>
                      <tr style={{ background:'var(--s2)' }}>
                        {['SKU','Product','UoM','Ordered','Received','Unit Cost','Total'].map(h=>(
                          <th key={h} style={{ padding:'5px 8px',textAlign:['Ordered','Received','Unit Cost','Total'].includes(h)?'right':'left',fontSize:10,fontWeight:700,color:'var(--td)',borderBottom:'1px solid var(--border)' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(po.items||[]).map((it,j)=>(
                        <tr key={j} style={{ borderBottom:'1px solid var(--border)',background:j%2===0?'var(--surface)':'var(--s2)' }}>
                          <td style={{ padding:'5px 8px',fontFamily:'monospace',fontSize:10,color:'var(--primary)' }}>{it.product_sku}</td>
                          <td style={{ padding:'5px 8px',fontWeight:600 }}>{it.product_name}</td>
                          <td style={{ padding:'5px 8px',color:'var(--tm)' }}>{it.uom}</td>
                          <td style={{ padding:'5px 8px',textAlign:'right' }}>{it.quantity} {it.uom}</td>
                          <td style={{ padding:'5px 8px',textAlign:'right',color:it.received_qty>=it.quantity?'var(--success)':it.received_qty>0?'var(--warning)':'var(--td)' }}>{it.received_qty} {it.uom}</td>
                          <td style={{ padding:'5px 8px',textAlign:'right' }}>{fmt(it.unit_cost)}</td>
                          <td style={{ padding:'5px 8px',textAlign:'right',fontWeight:700 }}>{fmt(it.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
          {!data.orders.length && <div style={{ textAlign:'center',padding:20,color:'var(--td)' }}>No purchase orders found</div>}
        </div>
      </>}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// STOCK TRANSFERS REPORT
// ════════════════════════════════════════════════════════════════
function StockTransferReportTab({ branches, isOwner, myBranchId }) {
  const [data, setData] = useState(null);
  const [ld, setLd] = useState(false);
  const [dateFrom, setDateFrom] = useState(monthStart());
  const [dateTo, setDateTo] = useState(today());
  const [branchId, setBranchId] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedTrf, setExpandedTrf] = useState(null);

  const run = useCallback(async () => {
    setLd(true);
    try {
      const p = { date_from: dateFrom, date_to: dateTo };
      if (branchId) p.branch_id = branchId;
      if (statusFilter) p.status = statusFilter;
      const r = await reportAPI.stockTransferReport(p);
      setData(r.data);
    } catch { toast.error('Failed to load transfer report'); }
    finally { setLd(false); }
  }, [dateFrom, dateTo, branchId, statusFilter]);

  useEffect(() => { run(); }, []);

  const ST_COLORS = { draft:'#64748b',pending:'#2563eb',in_transit:'#d97706',received:'#16a34a',cancelled:'#dc2626' };
  const selectStyle = { padding:'7px 10px',borderRadius:8,border:'1px solid var(--border)',background:'var(--s2)',color:'var(--text)',fontSize:12 };

  return (
    <div>
      <DateRangeBar dateFrom={dateFrom} dateTo={dateTo} onFrom={setDateFrom} onTo={setDateTo}
        onRun={run} loading={ld} branches={branches} branchId={branchId} onBranch={setBranchId} isOwner={isOwner}>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={selectStyle}>
          <option value="">All Statuses</option>
          {['draft','pending','in_transit','received','cancelled'].map(s=><option key={s} value={s}>{s}</option>)}
        </select>
        {data && <ExportCSV data={data.transfers} filename="stock-transfers-report"
          columns={[{key:'transfer_number',label:'TRF#'},{key:'from_branch',label:'From'},{key:'to_branch',label:'To'},{key:'status',label:'Status'},{key:'created_at',label:'Created',render:r=>fmtDate(r.created_at)},{key:'total_qty',label:'Qty'}]}/>}
      </DateRangeBar>

      {data && <>
        <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))',gap:10,marginBottom:16 }}>
          <KpiCard icon={ArrowLeftRight} label="Total Transfers" value={data.total_transfers} color="var(--primary)"/>
          <KpiCard icon={Package} label="Total Items Moved" value={data.total_items} color="var(--accent)"/>
          <KpiCard icon={CheckCircle} label="Received" value={data.by_status.received||0} color="var(--success)"/>
          <KpiCard icon={Truck} label="In Transit" value={data.by_status.in_transit||0} color="var(--warning)"/>
        </div>

        <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:14,marginBottom:14 }}>
          <div className="card" style={{ padding:16 }}>
            <SectionTitle icon={ArrowLeftRight} color="var(--primary)">Transfers by Status</SectionTitle>
            <div style={{ display:'flex',flexWrap:'wrap',gap:8 }}>
              {Object.entries(data.by_status).map(([s,count]) => (
                <div key={s} style={{ flex:1,minWidth:90,textAlign:'center',padding:'10px 8px',borderRadius:8,background:'var(--s2)',border:'1px solid var(--border)' }}>
                  <div style={{ fontSize:20,fontWeight:800,color:ST_COLORS[s]||'var(--text)' }}>{count}</div>
                  <div style={{ fontSize:10,color:'var(--td)',textTransform:'capitalize',marginTop:2 }}>{s.replace('_',' ')}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="card" style={{ padding:16 }}>
            <SectionTitle icon={Building2} color="var(--accent)">Activity by Branch</SectionTitle>
            <Table cols={[
              {key:'branch',label:'Branch',bold:true},
              {key:'direction',label:'Direction'},
              {key:'transfers',label:'Transfers',right:true,bold:true},
              {key:'items',label:'Items Moved',right:true},
            ]} data={data.by_branch.slice(0,8)}/>
          </div>
        </div>

        {/* Transfers with expandable items */}
        <div className="card" style={{ padding:16 }}>
          <SectionTitle icon={FileText} color="var(--primary)">Transfer Details</SectionTitle>
          {data.transfers.map((trf,i) => (
            <div key={trf.id} style={{ marginBottom:6,border:'1px solid var(--border)',borderRadius:8,overflow:'hidden' }}>
              <button onClick={() => setExpandedTrf(expandedTrf===trf.id?null:trf.id)}
                style={{ width:'100%',display:'flex',alignItems:'center',gap:10,padding:'9px 12px',background:i%2===0?'var(--surface)':'var(--s2)',border:'none',cursor:'pointer',textAlign:'left' }}>
                <span style={{ fontFamily:'monospace',fontSize:11,color:'var(--primary)',fontWeight:700,minWidth:90 }}>{trf.transfer_number}</span>
                <span style={{ fontSize:12,fontWeight:600 }}>{trf.from_branch}</span>
                <span style={{ fontSize:12,color:'var(--primary)',fontWeight:700 }}>→</span>
                <span style={{ fontSize:12,fontWeight:600 }}>{trf.to_branch}</span>
                <span style={{ flex:1 }}/>
                <span style={{ fontSize:11,color:'var(--td)' }}>{fmtDate(trf.created_at)}</span>
                <StatusBadge status={trf.status}/>
                <span style={{ fontSize:12,fontWeight:700 }}>{trf.total_qty} units</span>
                {expandedTrf===trf.id ? <ChevronUp size={13}/> : <ChevronDown size={13}/>}
              </button>
              {expandedTrf===trf.id && (
                <div style={{ padding:'4px 8px 8px' }}>
                  <div style={{ display:'flex',gap:12,padding:'6px 4px',marginBottom:4,fontSize:11,color:'var(--td)' }}>
                    {trf.shipped_at && <span>Shipped: <strong>{fmtDate(trf.shipped_at)}</strong></span>}
                    {trf.received_at && <span>Received: <strong>{fmtDate(trf.received_at)}</strong></span>}
                  </div>
                  <table style={{ width:'100%',borderCollapse:'collapse',fontSize:12 }}>
                    <thead>
                      <tr style={{ background:'var(--s2)' }}>
                        {['SKU','Product','UoM','Qty Sent','Qty Received'].map(h=>(
                          <th key={h} style={{ padding:'5px 8px',textAlign:['Qty Sent','Qty Received'].includes(h)?'right':'left',fontSize:10,fontWeight:700,color:'var(--td)',borderBottom:'1px solid var(--border)' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(trf.items||[]).map((it,j)=>(
                        <tr key={j} style={{ borderBottom:'1px solid var(--border)',background:j%2===0?'var(--surface)':'var(--s2)' }}>
                          <td style={{ padding:'5px 8px',fontFamily:'monospace',fontSize:10,color:'var(--primary)' }}>{it.product_sku}</td>
                          <td style={{ padding:'5px 8px',fontWeight:600 }}>{it.product_name}</td>
                          <td style={{ padding:'5px 8px',color:'var(--tm)' }}>{it.uom}</td>
                          <td style={{ padding:'5px 8px',textAlign:'right',fontWeight:700 }}>{it.quantity} {it.uom}</td>
                          <td style={{ padding:'5px 8px',textAlign:'right',color:it.received_qty>=it.quantity?'var(--success)':it.received_qty>0?'var(--warning)':'var(--td)' }}>{it.received_qty} {it.uom}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
          {!data.transfers.length && <div style={{ textAlign:'center',padding:20,color:'var(--td)' }}>No transfers found</div>}
        </div>
      </>}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// VENDOR PAYMENTS REPORT
// ════════════════════════════════════════════════════════════════
function VendorPaymentReportTab({ branches, isOwner, myBranchId }) {
  const [data, setData] = useState(null);
  const [ld, setLd] = useState(false);
  const [dateFrom, setDateFrom] = useState(monthStart());
  const [dateTo, setDateTo] = useState(today());
  const [branchId, setBranchId] = useState('');
  const [vendors, setVendors] = useState([]);
  const [vendorId, setVendorId] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('');
  const [expanded, setExpanded] = useState(null);

  useEffect(() => { vendorAPI.getAll({ limit:200 }).then(r => setVendors(r.data.data||r.data)).catch(()=>{}); }, []);

  const run = useCallback(async () => {
    setLd(true);
    try {
      const p = { date_from: dateFrom, date_to: dateTo };
      if (branchId) p.branch_id = branchId;
      if (vendorId) p.vendor_id = vendorId;
      if (paymentFilter) p.payment_status = paymentFilter;
      const r = await reportAPI.vendorPaymentReport(p);
      setData(r.data);
    } catch { toast.error('Failed to load vendor payment report'); }
    finally { setLd(false); }
  }, [dateFrom, dateTo, branchId, vendorId, paymentFilter]);

  useEffect(() => { run(); }, []);

  const PAY_COLORS = { unpaid:'#dc2626', partial_paid:'#d97706', paid:'#16a34a' };
  const PAY_LABELS = { unpaid:'Unpaid', partial_paid:'Part Paid', paid:'Paid' };
  const selectStyle = { padding:'7px 10px',borderRadius:8,border:'1px solid var(--border)',background:'var(--s2)',color:'var(--text)',fontSize:12 };

  return (
    <div>
      <DateRangeBar dateFrom={dateFrom} dateTo={dateTo} onFrom={setDateFrom} onTo={setDateTo}
        onRun={run} loading={ld} branches={branches} branchId={branchId} onBranch={setBranchId} isOwner={isOwner}>
        <select value={vendorId} onChange={e => setVendorId(e.target.value)} style={selectStyle}>
          <option value="">All Vendors</option>
          {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
        <select value={paymentFilter} onChange={e => setPaymentFilter(e.target.value)} style={selectStyle}>
          <option value="">All Payments</option>
          {['unpaid','partial_paid','paid'].map(s=><option key={s} value={s}>{PAY_LABELS[s]}</option>)}
        </select>
        {data && <ExportCSV data={data.vendors} filename="vendor-payments-report"
          columns={[{key:'name',label:'Vendor'},{key:'orders',label:'Orders'},{key:'total_ordered',label:'Ordered',render:r=>fmtNum(r.total_ordered)},{key:'total_received',label:'Received',render:r=>fmtNum(r.total_received)},{key:'total_paid',label:'Paid',render:r=>fmtNum(r.total_paid)},{key:'total_pending',label:'Outstanding',render:r=>fmtNum(r.total_pending)},{key:'payment_terms',label:'Terms'}]}/>}
      </DateRangeBar>

      {data && <>
        <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))',gap:10,marginBottom:16 }}>
          <KpiCard icon={Truck} label="Vendors" value={data.total_vendors} color="var(--primary)"/>
          <KpiCard icon={DollarSign} label="Total Ordered" value={`$${fmtNum(data.grand_total.total_ordered)}`} color="var(--accent)"/>
          <KpiCard icon={CheckCircle} label="Total Received" value={`$${fmtNum(data.grand_total.total_received)}`} color="var(--success)"/>
          <KpiCard icon={CheckCircle} label="Total Paid" value={`$${fmtNum(data.grand_total.total_paid)}`} color="var(--primary)"/>
          <KpiCard icon={AlertTriangle} label="Outstanding" value={`$${fmtNum(data.grand_total.total_pending)}`} color={data.grand_total.total_pending>0?'var(--danger)':'var(--success)'}/>
        </div>

        <div className="card" style={{ padding:16,marginBottom:14 }}>
          <SectionTitle icon={Truck} color="var(--primary)">Vendor Payment Summary</SectionTitle>
          <Table cols={[
            {key:'name',label:'Vendor',bold:true},
            {key:'payment_terms',label:'Terms'},
            {key:'orders',label:'Orders',right:true},
            {key:'total_ordered',label:'Ordered',right:true,bold:true,render:r=>fmt(r.total_ordered)},
            {key:'total_received',label:'Received',right:true,render:r=>fmt(r.total_received)},
            {key:'total_paid',label:'Paid',right:true,render:r=><span style={{color:'var(--success)',fontWeight:700}}>{fmt(r.total_paid)}</span>},
            {key:'total_pending',label:'Outstanding',right:true,bold:true,render:r=><span style={{color:r.total_pending>0?'var(--danger)':'var(--success)',fontWeight:800}}>{fmt(r.total_pending)}</span>},
          ]} data={data.vendors}/>
        </div>

        <div className="card" style={{ padding:16 }}>
          <SectionTitle icon={FileText} color="var(--accent)">PO Breakdown by Vendor</SectionTitle>
          {data.vendors.map(v => (
            <div key={v.id} style={{ marginBottom:8,border:'1px solid var(--border)',borderRadius:8,overflow:'hidden' }}>
              <button onClick={() => setExpanded(expanded===v.id?null:v.id)}
                style={{ width:'100%',display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 14px',background:'var(--s2)',border:'none',cursor:'pointer' }}>
                <div style={{ display:'flex',alignItems:'center',gap:12 }}>
                  <span style={{ fontWeight:700,fontSize:13 }}>{v.name}</span>
                  <span style={{ fontSize:11,color:'var(--td)' }}>{v.orders} orders</span>
                  {v.payment_terms && <span style={{ fontSize:10,color:'var(--tm)',background:'var(--border)',padding:'1px 6px',borderRadius:4 }}>{v.payment_terms}</span>}
                </div>
                <div style={{ display:'flex',alignItems:'center',gap:16 }}>
                  <span style={{ fontSize:12 }}>Ordered: <strong>{fmt(v.total_ordered)}</strong></span>
                  <span style={{ fontSize:12,color:'var(--success)' }}>Paid: <strong>{fmt(v.total_paid)}</strong></span>
                  <span style={{ fontSize:12,color:v.total_pending>0?'var(--danger)':'var(--success)' }}>Outstanding: <strong>{fmt(v.total_pending)}</strong></span>
                  {expanded===v.id ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
                </div>
              </button>
              {expanded===v.id && (
                <div style={{ padding:'0 4px 8px' }}>
                  <table style={{ width:'100%',borderCollapse:'collapse',fontSize:12 }}>
                    <thead>
                      <tr style={{ background:'var(--s2)' }}>
                        {['PO #','Date','Rcv Status','Pay Status','Ordered','Received','Paid','Outstanding'].map(h=>(
                          <th key={h} style={{ padding:'6px 10px',textAlign:['Ordered','Received','Paid','Outstanding'].includes(h)?'right':'left',fontSize:10,fontWeight:700,color:'var(--td)',borderBottom:'1px solid var(--border)' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {v.pos.map((po,j)=>(
                        <tr key={j} style={{ borderBottom:'1px solid var(--border)',background:j%2===0?'var(--surface)':'var(--s2)' }}>
                          <td style={{ padding:'6px 10px',fontFamily:'monospace',fontSize:11,color:'var(--primary)',fontWeight:700 }}>{po.po_number}</td>
                          <td style={{ padding:'6px 10px',fontSize:11,color:'var(--td)' }}>{fmtDate(po.order_date)}</td>
                          <td style={{ padding:'6px 10px' }}><StatusBadge status={po.status}/></td>
                          <td style={{ padding:'6px 10px' }}>
                            <span style={{ padding:'2px 7px',borderRadius:10,fontSize:10,fontWeight:700,background:PAY_COLORS[po.payment_status]+'22',color:PAY_COLORS[po.payment_status] }}>
                              {PAY_LABELS[po.payment_status]||po.payment_status}
                            </span>
                          </td>
                          <td style={{ padding:'6px 10px',textAlign:'right',fontWeight:700 }}>{fmt(po.ordered)}</td>
                          <td style={{ padding:'6px 10px',textAlign:'right' }}>{fmt(po.received)}</td>
                          <td style={{ padding:'6px 10px',textAlign:'right',color:'var(--success)',fontWeight:700 }}>{fmt(po.paid)}</td>
                          <td style={{ padding:'6px 10px',textAlign:'right',fontWeight:800,color:po.pending>0?'var(--danger)':'var(--success)' }}>{fmt(po.pending)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      </>}
    </div>
  );
}

// ── Expense Report ─────────────────────────────────────────────
function ExpenseReportTab({ branches, isOwner, myBranchId }) {
  const fmtAmt = n => '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const ColorDot = ({ color }) => <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: color || '#6366f1', marginRight: 5, flexShrink: 0 }} />;

  const [data, setData]         = useState(null);
  const [list, setList]         = useState([]);
  const [cats, setCats]         = useState([]);
  const [ld, setLd]             = useState(false);
  const [ldList, setLdList]     = useState(false);
  const [filters, setFilters]   = useState({
    from_date: monthStart(),
    to_date: today(),
    branch_id: (!isOwner && myBranchId) ? String(myBranchId) : '',
    category_id: '',
    status: 'approved',
  });
  const [listPage, setListPage] = useState(1);
  const LIST_LIMIT = 30;

  const branchOpts = [{ value: '', label: 'All Branches' }, ...branches.map(b => ({ value: String(b.id), label: `${b.name} (${b.code})` }))];

  const loadSummary = useCallback(async () => {
    setLd(true);
    try {
      const p = { ...filters };
      Object.keys(p).forEach(k => { if (!p[k]) delete p[k]; });
      const r = await expenseAPI.report(p);
      setData(r.data);
    } catch { toast.error('Failed to load expense report'); }
    finally { setLd(false); }
  }, [filters]);

  const loadList = useCallback(async () => {
    setLdList(true);
    try {
      const p = { ...filters, page: listPage, limit: LIST_LIMIT };
      Object.keys(p).forEach(k => { if (!p[k]) delete p[k]; });
      const r = await expenseAPI.getAll(p);
      setList(r.data.data || []);
    } catch { }
    finally { setLdList(false); }
  }, [filters, listPage]);

  useEffect(() => {
    expenseCategoryAPI.getAll({}).then(r => setCats(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => { loadSummary(); loadList(); }, [filters, listPage]);

  const catOpts = [{ value: '', label: 'All Categories' }, ...cats.map(c => ({ value: String(c.id), label: c.name }))];
  const statusOpts = [{ value: '', label: 'All Statuses' }, { value: 'draft', label: 'Draft' }, { value: 'approved', label: 'Approved' }, { value: 'rejected', label: 'Rejected' }];

  const grandTotal   = parseFloat(data?.totals?.grand_total || 0);
  const grandCount   = parseInt(data?.totals?.grand_count || 0);
  const byBranch     = data?.byBranch || [];
  const byCategory   = data?.byCategory || [];
  const trend        = data?.trend || [];

  // Build chart data for monthly trend (aggregate across branches)
  const trendChartData = (() => {
    const monthMap = {};
    trend.forEach(r => {
      const m = String(r.dataValues?.month || r.month || '');
      const total = parseFloat(r.dataValues?.total || r.total || 0);
      if (!monthMap[m]) monthMap[m] = { month: m, total: 0 };
      monthMap[m].total += total;
    });
    return Object.values(monthMap).sort((a, b) => a.month.localeCompare(b.month)).map(r => ({
      ...r,
      label: r.month ? new Date(r.month).toLocaleDateString('en-US', { month: 'short', year: '2-digit' }) : '—',
    }));
  })();

  // Per-branch trend chart data
  const branchTrendData = (() => {
    const months = [...new Set(trend.map(r => String(r.dataValues?.month || r.month || '')))].sort();
    const branchMap = new Map(trend.map(r => [r.branch_id, r.branch?.name || String(r.branch_id)]));
    return months.map(m => {
      const row = { label: m ? new Date(m).toLocaleDateString('en-US', { month: 'short', year: '2-digit' }) : '—' };
      branchMap.forEach((name, bid) => {
        const found = trend.find(r => String(r.dataValues?.month || r.month) === m && r.branch_id === bid);
        row[name] = found ? parseFloat(found.dataValues?.total || found.total || 0) : 0;
      });
      return row;
    });
  })();
  const branchNames = [...new Map(trend.map(r => [r.branch_id, r.branch?.name || String(r.branch_id)])).values()];

  // CSV export
  const exportCSV = () => {
    if (!list.length) return toast.error('No data to export');
    const hdr = 'Number,Date,Branch,Category,Title,Amount USD,Currency,Original Amount,Payment,Status,Creator';
    const rows = list.map(r => [
      r.expense_number, r.expense_date, r.branch?.name || '', r.category?.name || '',
      `"${r.title}"`, r.amount_usd, r.currency, r.amount,
      r.payment_method, r.status, `${r.creator?.first_name || ''} ${r.creator?.last_name || ''}`.trim()
    ].join(','));
    const csv = [hdr, ...rows].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a'); a.href = url; a.download = `expenses-${today()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const SF = (k, v) => { setFilters(f => ({ ...f, [k]: v })); setListPage(1); };

  return (
    <div>
      {/* Filter bar */}
      <div className="card" style={{ padding: '12px 14px', marginBottom: 16, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        {isOwner && (
          <div style={{ minWidth: 150 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--td)', marginBottom: 4 }}>Branch</div>
            <select value={filters.branch_id} onChange={e => SF('branch_id', e.target.value)}
              style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', fontSize: 12 }}>
              {branchOpts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        )}
        <div style={{ minWidth: 150 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--td)', marginBottom: 4 }}>Category</div>
          <select value={filters.category_id} onChange={e => SF('category_id', e.target.value)}
            style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', fontSize: 12 }}>
            {catOpts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div style={{ minWidth: 120 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--td)', marginBottom: 4 }}>Status</div>
          <select value={filters.status} onChange={e => SF('status', e.target.value)}
            style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', fontSize: 12 }}>
            {statusOpts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--td)', marginBottom: 4 }}>From</div>
          <input type="date" value={filters.from_date} onChange={e => SF('from_date', e.target.value)}
            style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', fontSize: 12 }} />
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--td)', marginBottom: 4 }}>To</div>
          <input type="date" value={filters.to_date} onChange={e => SF('to_date', e.target.value)}
            style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', fontSize: 12 }} />
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => { loadSummary(); loadList(); }} disabled={ld} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          {ld ? <RefreshCw size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <RefreshCw size={12} />} Refresh
        </button>
        <button className="btn btn-ghost btn-sm" onClick={exportCSV} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <Download size={12} /> Export CSV
        </button>
      </div>

      {/* KPI cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(170px,1fr))', gap: 12, marginBottom: 18 }}>
        <KpiCard icon={TrendingDown} label="Total Expenses"    value={fmtAmt(grandTotal)} sub={`${grandCount} records`}              color="var(--danger)" />
        <KpiCard icon={Building2}   label="Branches"           value={byBranch.length}   sub="reporting branches"                    color="var(--primary)" />
        <KpiCard icon={Tag}         label="Categories"         value={byCategory.length} sub="active categories"                     color="#f59e0b" />
        <KpiCard icon={TrendingUp}  label="Avg per Expense"    value={grandCount ? fmtAmt(grandTotal / grandCount) : '$0.00'} sub="average amount" color="#8b5cf6" />
      </div>

      {/* Charts row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
        {/* Monthly trend bar chart */}
        <div className="card" style={{ padding: 16 }}>
          <SectionTitle icon={TrendingDown} color="var(--danger)">Monthly Expense Trend</SectionTitle>
          {trendChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={trendChartData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={v => '$' + (v >= 1000 ? (v / 1000).toFixed(1) + 'k' : v)} />
                <Tooltip formatter={v => ['$' + Number(v).toFixed(2), 'Total']} />
                <Bar dataKey="total" fill="var(--danger)" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--td)', fontSize: 13 }}>No trend data</div>}
        </div>

        {/* By category pie */}
        <div className="card" style={{ padding: 16 }}>
          <SectionTitle icon={Tag} color="#f59e0b">Expenses by Category</SectionTitle>
          {byCategory.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={byCategory.map(r => ({
                  name: r.category?.name || 'Unknown',
                  value: parseFloat(r.dataValues?.total || r.total || 0),
                  color: r.category?.color || '#6366f1',
                }))} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80}>
                  {byCategory.map((r, i) => <Cell key={i} fill={r.category?.color || COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={v => '$' + Number(v).toFixed(2)} />
                <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--td)', fontSize: 13 }}>No category data</div>}
        </div>
      </div>

      {/* Branch breakdown + Category table side by side */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
        {/* By Branch */}
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', fontWeight: 700, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Building2 size={14} color="var(--primary)" /> Breakdown by Branch
          </div>
          <div>
            {byBranch.length ? byBranch.map(row => {
              const total = parseFloat(row.dataValues?.total || row.total || 0);
              const count = parseInt(row.dataValues?.count || row.count || 0);
              const pct = grandTotal > 0 ? (total / grandTotal * 100) : 0;
              return (
                <div key={row.branch_id} style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Building2 size={12} color="var(--td)" />
                      <span style={{ fontWeight: 600, fontSize: 13 }}>{row.branch?.name || '—'}</span>
                      <span style={{ fontSize: 10, color: 'var(--td)', background: 'var(--s2)', padding: '1px 5px', borderRadius: 4 }}>{row.branch?.code}</span>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontWeight: 800, fontSize: 13, color: 'var(--danger)' }}>{fmtAmt(total)}</div>
                      <div style={{ fontSize: 10, color: 'var(--td)' }}>{count} expenses</div>
                    </div>
                  </div>
                  <div style={{ height: 5, background: 'var(--s2)', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ height: '100%', background: 'var(--primary)', width: `${pct}%`, transition: 'width .3s', borderRadius: 3 }} />
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--td)', marginTop: 2 }}>{pct.toFixed(1)}% of total</div>
                </div>
              );
            }) : <div style={{ padding: 30, textAlign: 'center', color: 'var(--td)', fontSize: 13 }}>No branch data</div>}
          </div>
        </div>

        {/* By Category table */}
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', fontWeight: 700, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Tag size={14} color="#f59e0b" /> Breakdown by Category
          </div>
          <div>
            {byCategory.length ? byCategory
              .sort((a, b) => parseFloat(b.dataValues?.total || b.total || 0) - parseFloat(a.dataValues?.total || a.total || 0))
              .map(row => {
                const cat = row.category;
                const total = parseFloat(row.dataValues?.total || row.total || 0);
                const count = parseInt(row.dataValues?.count || row.count || 0);
                const pct = grandTotal > 0 ? (total / grandTotal * 100) : 0;
                return (
                  <div key={row.category_id} style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <ColorDot color={cat?.color} />
                        <span style={{ fontWeight: 600, fontSize: 13 }}>{cat?.name || '—'}</span>
                        {!cat?.branch_id && <span style={{ fontSize: 9, color: 'var(--primary)', background: 'var(--pm)', padding: '1px 5px', borderRadius: 3, display: 'flex', alignItems: 'center', gap: 2 }}><Globe size={8} />Global</span>}
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontWeight: 800, fontSize: 13, color: 'var(--danger)' }}>{fmtAmt(total)}</div>
                        <div style={{ fontSize: 10, color: 'var(--td)' }}>{count} expenses</div>
                      </div>
                    </div>
                    <div style={{ height: 5, background: 'var(--s2)', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ height: '100%', background: cat?.color || '#6366f1', width: `${pct}%`, transition: 'width .3s', borderRadius: 3 }} />
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--td)', marginTop: 2 }}>{pct.toFixed(1)}% of total</div>
                  </div>
                );
              }) : <div style={{ padding: 30, textAlign: 'center', color: 'var(--td)', fontSize: 13 }}>No category data</div>}
          </div>
        </div>
      </div>

      {/* Per-branch trend line chart (owner/multi-branch) */}
      {isOwner && branchNames.length > 1 && branchTrendData.length > 0 && (
        <div className="card" style={{ padding: 16, marginBottom: 18 }}>
          <SectionTitle icon={TrendingDown} color="var(--danger)">Monthly Trend per Branch</SectionTitle>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={branchTrendData} margin={{ top: 0, right: 20, left: 0, bottom: 0 }}>
              <XAxis dataKey="label" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={v => '$' + (v >= 1000 ? (v / 1000).toFixed(1) + 'k' : v)} />
              <Tooltip formatter={v => '$' + Number(v).toFixed(2)} />
              <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
              {branchNames.map((name, i) => (
                <Line key={name} type="monotone" dataKey={name} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={false} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Expense list table */}
      <div className="card" style={{ padding: 16 }}>
        <SectionTitle icon={FileText} color="var(--primary)">Expense Records</SectionTitle>
        <Table
          cols={[
            { key: 'expense_number', label: '#', render: r => <span style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--td)' }}>{r.expense_number}</span> },
            { key: 'expense_date',   label: 'Date',     render: r => <span style={{ fontSize: 12 }}>{r.expense_date}</span> },
            { key: 'branch',         label: 'Branch',   render: r => <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Building2 size={11} color="var(--td)" /><span style={{ fontSize: 12 }}>{r.branch?.name || '—'}</span></div> },
            { key: 'category',       label: 'Category', render: r => r.category ? <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 8, height: 8, borderRadius: '50%', background: r.category.color || '#6366f1', display: 'inline-block', flexShrink: 0 }} /><span style={{ fontSize: 12 }}>{r.category.name}</span></div> : '—' },
            { key: 'title',          label: 'Title',    render: r => <span style={{ fontWeight: 600, fontSize: 12 }}>{r.title}</span> },
            { key: 'amount_usd',     label: 'Amount (USD)', right: true, bold: true, render: r => <span style={{ color: 'var(--danger)', fontWeight: 700 }}>{fmtAmt(r.amount_usd)}</span> },
            { key: 'payment_method', label: 'Payment',  render: r => <span style={{ fontSize: 11, textTransform: 'capitalize' }}>{(r.payment_method || '').replace('_', ' ')}</span> },
            { key: 'status',         label: 'Status',   render: r => <StatusBadge status={r.status} /> },
            { key: 'creator',        label: 'By',       render: r => <span style={{ fontSize: 11 }}>{r.creator ? `${r.creator.first_name} ${r.creator.last_name}` : '—'}</span> },
          ]}
          data={list}
          emptyMsg={ldList ? 'Loading...' : 'No expenses found'}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10, gap: 6 }}>
          {listPage > 1 && <button className="btn btn-ghost btn-sm" onClick={() => setListPage(p => p - 1)}>← Prev</button>}
          <span style={{ fontSize: 12, color: 'var(--td)', padding: '6px 0' }}>Page {listPage}</span>
          {list.length === LIST_LIMIT && <button className="btn btn-ghost btn-sm" onClick={() => setListPage(p => p + 1)}>Next →</button>}
        </div>
      </div>
    </div>
  );
}

function VendorReportTab({ branches, isOwner }) {
  const [data, setData] = useState(null); const [ld, setLd] = useState(false);
  const { t } = useLang();
  const { hasPermission } = useAuth();
  const canSeeCost = hasPermission('cost_price.view');
  const run = async () => { setLd(true); try { const r = await reportAPI.vendorSummary({}); setData(r.data); } catch { toast.error('Failed') } finally { setLd(false) } };
  useEffect(() => { run() }, []);
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button className="btn btn-primary btn-sm" onClick={run} disabled={ld} style={{ gap: 5 }}>{ld ? <RefreshCw size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <RefreshCw size={12} />} Refresh</button>
      </div>
      {data && <>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))', gap: 10, marginBottom: 16 }}>
          <KpiCard icon={Building2} label={t('reports.totalVendors')} value={data.total_vendors} color="var(--primary)" />
        </div>
        <div className="card" style={{ padding: 16 }}>
          <SectionTitle icon={Building2} color="var(--primary)">{t('reports.vendorSummary')}</SectionTitle>
          <Table cols={[{ key: 'name', label: 'Vendor', bold: true }, { key: 'phone', label: 'Phone' }, { key: 'email', label: 'Email' }, { key: 'products_count', label: 'Products', right: true, bold: true }, { key: 'total_stock', label: 'Total Stock', right: true, render: r => r.total_stock.toFixed(0) }, ...(canSeeCost?[{ key: 'total_cost_value', label: 'Stock Value', right: true, bold: true, render: r => fmt(r.total_cost_value) }]:[])]} data={data.vendors} />
        </div>
      </>}
    </div>
  );
}
